import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { inset, radius, rgba, semantic, space, typeRole, weight } from '@repo/design-tokens';
import { useEquipmentCatalogStore, useFloorplanStore } from '@repo/front-end-state';
import {
  asArray,
  capitaliseFirstLetter,
  findLatestDesign,
  fileableFloorBids,
  floorTargetRefid,
  heatBlobOutlines,
  isLive,
  layoutCurrentDesigns,
  normaliseServerMessage,
  observerTargetNamesByRefid,
  planSearchEntry,
  surfaceName,
} from '@repo/front-end-utils';
import {
  getHeatGridCameraOutcome,
  getObserverProposalsOutcome,
  HEAT_GRID_READ_REASONS,
  MIDDLEWARE_REQUEST_TIMEOUT_MS,
  OBSERVER_PROPOSALS_READ_REASONS,
} from '@repo/front-end-services';
import type { HeatGridOutcome, ObserverProposalsOutcome } from '@repo/front-end-services';
import {
  suggestionLabel,
  suggestionView,
  type SuggestionReadState,
} from './observerWorkspace/suggestionModel';
import MachineList from './observerWorkspace/MachineList';
import {
  nextUnsettled,
  regionShape,
  reviewMachines,
  type DiscardReason,
  type ReviewMachine,
} from './observerWorkspace/machineStateModel';

/** How a discard's reason is announced (spec `observer-autogen-review-discard-and-scores`, Block A). */
const DISCARD_REASON_TEXT: Record<DiscardReason, string> = {
  'wrong-place': 'wrong place',
  'wrong-machine': 'wrong machine',
  'not-a-machine': 'not a machine',
};
import {
  applySuggestion,
  liveObserverState,
  takenProposals,
  targetsWithNewObservers,
} from './observerWorkspace/acceptSuggestion';
import { useSuggestionDecisionStore } from './observerWorkspace/suggestionDecisions';
import type { DraftEntityChanges, HeatBlobOutline, HeatGridCells } from '@repo/front-end-utils';
import type { HeatGridCameraResponse, HeatGridWindow } from '@repo/frontend-types';
import type { FloorPlannerObserver, ObserverTrackSide, RTSPEndpoint } from '@repo/frontend-types';
import { Banner, Button, Icon, ListPanel, Modal, Tag, useToast } from '@repo/design-system';
import { useCameraWallFeeds } from '@repo/ui';
import type { CanvasMarker } from '@repo/ui/planner';

import Config from '../../common/Config';
import PolygonPlotter2, {
  OBSERVER_PLOTTER_ATTRIBUTE,
  type MachineOutline,
  type Polygon,
  type PolygonPlotterHandle,
  type SuggestionOutline,
  type PolygonTip,
} from '../../components/PolygonPlotter2/PolygonPlotter2';
import { liveObserverId, storedOrdinalById } from './buildConfigurationStatement';
import ObserverInventoryPanel, {
  SettledText,
  attentionText,
  type ObserverTargetKind,
} from './ObserverInventoryPanel';
import LiveConfigurationNotices from './LiveConfigurationNotices';
import NoticesChip from './observerWorkspace/NoticesChip';
import type { LiveTwinConfiguration } from './useLiveTwinConfiguration';
import ObserverSideBySide from './ObserverSideBySide';
import { GLASS_EDGE, GLASS_START, LIST_PANEL_WIDTH, PANEL_TOP } from './glassLayout';
import ObserverStage, {
  AddCameraTile,
  STAGE_ROOT_ATTRIBUTE,
  STAGE_TOAST_REGION,
  createHoverStore,
  useHovered,
} from './ObserverStage';
import type { HoverStore, StageFloor, StageHoverText } from './ObserverStage';
import {
  GROUP_LOOK,
  PLAN_VIEW,
  STATUS_DOT,
  cameraStepBack,
  cameraSticks,
  removeEntityFromCamera,
  resolveStageView,
  stageViewForPick,
  stepBack,
} from './observerStageModel';
import type { StageView } from './observerStageModel';
import {
  addedRefidsFrom,
  classifyEntityAttention,
  computeModelChanges,
  NEEDS_ATTENTION,
} from './observerAttention';
import type { DraftComparisonState } from './useDraftChanges';
import { computeCoverage, liveReadState, planEntities, planMeasures } from './entityCoverage';
import { buildEntityNamer } from './entityCodeLabel';
import { useObserverStampStore } from './observerStamps';
import {
  DEFAULT_TRACK_SIDE,
  NEW_OBSERVER_TRACK_SIDE,
  trackSideLabel,
  buildFloorEntityIndex,
  buildObserversByFloor,
  hydrateObserverSession,
  observerRefid,
  backdropPolygonsOnCamera,
  endpointOfRegion,
  regionsOnFeed,
  isOnCamera,
  byFiledOrder,
  linkRegion,
  mergeCameraPolygons,
  observerCameraGroups,
  observerTip,
  polygonsOnCamera,
  polygonIdFromObserverRefid,
  geometryOf,
} from './observerSessionState';
import { useZoomRegionsByCamera } from './useZoomRegionsByCamera';
import type {
  ObserverCameraGroup,
  ObserverStateByRefid,
  ObserverTip,
} from './observerSessionState';
import { feedSight, scoreFeedsForEntity } from './observerSourceRanking';
import { PlanEntitySearch } from './PlanEntitySearch';
import type { PlanSearchEntry } from '@repo/front-end-utils';
import { useJoinedPlanSearchIndex } from './usePlanSearchSources';
import {
  GRID_HEAT_CAPTIONS,
  gridHeatCaption,
  gridHeatChip,
  gridHeatSubline,
  gridMissingNote,
} from './heatCaption';
import type { GridHeatReadState } from './heatCaption';
import {
  decodeHeatView,
  HEAT_VIEW_PARTS,
  heatViewForTrackSide,
  mergeHeatPartAnswers,
  type HeatView,
} from './heatView';
import {
  HEAT_GRID_CELL_PX,
  useHeatGridImage,
} from '../../components/PolygonPlotter2/heatGridImage';
import { markHeatGrid } from '../../components/PolygonPlotter2/heatGridTiming';
import { clockTime } from './DraftStatusTag';
import { ObserverCameraView } from './ObserverCameraView';
import {
  ADD_OBSERVER_ARMED,
  ADD_OBSERVER_CANCELLED,
  CONFIRMED_TEXT,
  EntityCard,
  type EntityCameraChip,
} from './observerWorkspace/EntityCard';
import { TrackingCard } from './observerWorkspace/TrackingCard';
import { PictureDock, createZoomReadout } from './observerWorkspace/PictureDock';
import { ShortcutCard } from './observerWorkspace/ShortcutCard';
import { stepGuideFor } from './observerWorkspace/stepGuideModel';
import { changeLine, nextAnnouncement, type ObserverChange } from './observerWorkspace/changeText';
import { createUndoStack, type ObserverSessionSnapshot } from './observerSessionState';
import { useHeatSettings } from './heatSettings';

/** Stable empty list, so a stream read that is loading or failed does not churn the memos below. */
const NO_STREAMS: RTSPEndpoint[] = [];
const NO_HEAT_OUTLINES: HeatBlobOutline[] = [];
/** What the step says of the heat outlines on show (`observer-matching-beyond-geometry-heat-outlines`, R7). */
export const heatOutlineCountText = (count: number, edge: number): string =>
  count === 0
    ? `No heat above ${edge}\u2009%`
    : `${count} heat outline${count === 1 ? '' : 's'} at ${edge}\u2009%`;
/** What the title-bar search indexes from the plan; its feeds are added as `extra`. */
const OBSERVER_SEARCH_KINDS = ['surface', 'item'] as const;
/** What the plotter's empty canvas says once the picture has definitively failed (FR10). */
export const NO_PICTURE_MESSAGE = 'No picture for this feed — see the notice above';

/**
 * A server-supplied fragment, rendered as a sentence.
 *
 * The middleware's reasons are lowercase and unpunctuated — they were written to be logged, not
 * read — and this puts them under a title that already ends in a full stop. Without this the editor
 * says "This camera's picture could not be loaded. no edge device is attached to this RTSP stream",
 * which reads as a bug in the app rather than a fact about the camera.
 */
const sentence = (text: string, fallback: string): string => {
  const trimmed = normaliseServerMessage(text, fallback).replace(/\s*\.?$/, '');
  return trimmed ? `${capitaliseFirstLetter(trimmed)}.` : '';
};

interface Props {
  className?: string;
  /** Onboarding session whose plan carries the placed zones and equipment. */
  sessionId: string;
  /**
   * Called after every operator edit, once the edit is in the plan store (`observers-page-draft`,
   * FR1/FR3). The host's draft saver persists the plan from here; this stage never saves on its
   * own.
   */
  onObserverEdit?: () => void;
  /**
   * Called synchronously the moment an edit is made, before its write-through frame runs. Until
   * `onObserverEdit` follows, the plan store does not yet hold the edit, so a save that lands in
   * between must not mark the store clean.
   */
  onObserverEditPending?: () => void;
  /**
   * Registers this stage's held-back write with the wizard, which runs it before any draft swap
   * (`twin-details-page-versions`, R8.3): Versions can swap the draft with this step on screen, and an
   * edit still waiting for its frame would otherwise miss the save the swap makes first. Returns the
   * unregister.
   */
  registerPendingWriteFlush?: (flush: () => void) => () => void;
  /**
   * Asks the wizard to shrink its step list to numbers (`true`) or show it whole (`false`) — called
   * when cameras go on or come off the stage (`observers-page-stage`, FR5). The wizard decides
   * whether this step may collapse it at all.
   */
  onNavCollapseWanted?: (collapsed: boolean) => void;
  /** To step 4, where feeds are added — offered when the twin has none (*Failure States*). */
  onGoToCameraFeeds?: () => void;
  /**
   * The one-bar header's slot for the draft notices (`observer-autogen-review-layout`, L2). Given,
   * the notices open from a chip there instead of a block above the stage; absent (every step-level
   * test, any other mount), they render above the stage as before.
   */
  noticesSlot?: HTMLElement | null;
  /** The step's launch note, which the notices chip's panel leads with (L2.2). */
  launchNote?: string;
  /**
   * The twin's live configuration, as `useLiveTwinConfiguration` reports it. Mounted on the page
   * rather than here, because FR9 requires the load on every entry route and this stage mounts only
   * when the operator reaches step 5.
   *
   * Four things are read from it, and each is a requirement rather than a convenience:
   *
   * - **`loadNonce`** re-hydrates this stage's editing state. Hydration latches after its first
   *   run, so a load landing AFTER the stage mounted would otherwise be invisible — the deep-link
   *   case, and every retry. Re-reading the store at that moment is correct rather than
   *   destructive, because the store holds whatever the match decided: the running set, or — since
   *   `observers-page-draft` (FR9; maria@twinlabs.ai 2026-10-02, superseding Rob's 2026-09-08 "live
   *   wins on every open") — the plan's own saved draft, kept. Every edit is in the store already.
   * - **`streams`** names a loaded observer's camera. The plan's own camera list cannot: the test
   *   twin has 151 observers and no placed cameras at all.
   * - **`config` / `observers`** say whether the counts in the inventory panel are yet an answer.
   * - **the whole set**, plus `retry`, is what `LiveConfigurationNotices` renders.
   *
   * Optional so the stage still renders standalone, with every read reported as still loading.
   */
  liveConfiguration?: LiveTwinConfiguration;
  /**
   * The SAVED plan's id (`twin_plan.id`), for the read-only *Suggestions* layer
   * (`observer-autogen-review-ui-preview`): the engine reads the saved plan by id. Absent or `null`
   * (no plan saved yet, or a step-level test), the layer is not offered.
   */
  planId?: number | null;
  /**
   * The draft compared with production, entity by entity (the page's change source,
   * `useDraftChanges`), and how that comparison stands. Only a `ready` comparison marks anything
   * "added in this draft" (`onboarding-status-colours-observers`, R2); absent, nothing is.
   */
  draftChanges?: DraftEntityChanges;
  draftComparison?: DraftComparisonState;
}

interface SelectedEntity {
  refid: string;
  /** `'floor'` for a floor-wide observer's target (`refiling-preserves-configuration`). */
  kind: ObserverTargetKind;
  label: string;
}

/*
 * The camera view's own pieces. The drawing area sits on the stage (`observers-page-camera-stage`);
 * the pop-up editor these were once styled for is gone.
 */

/* The open entity's observers on this camera, as a single tab-stop strip in the entity card. One tile per
   observer; only rendered where the entity carries more than one, because a one-item chooser is
   noise on the overwhelming majority of entities. */
const ObserverStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[1]};
`;

/*
 * The camera's messages: they stack as before, inside the status strip over the picture's foot
 * (`observer-autogen-review-layout`, L3.3), so a message arriving mid-draw no longer needs a
 * reserved row to keep the picture still — it never takes the picture's height at all.
 */
const MessageRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  &:empty {
    display: none;
  }
`;

/**
 * The camera view's status line: which feed is being drawn on, Refresh's answers, a link said.
 * Quiet by design — it is a confirmation, not a warning.
 */
const SelectedFeedStatus = styled.div`
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.muted};
  min-height: ${typeRole.caption1.lineHeight};
`;

const PlotterHost = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/** The heat history's labels over the picture: which heat, and the days it does not cover (card 25). */
/** Whether a ready grid answer's cells for `view` decode; one that does not is never cached. */
const gridAnswerDecodes = (value: HeatGridCameraResponse, view: HeatView): boolean => {
  try {
    decodeHeatView(value, view);
    return true;
  } catch {
    return false;
  }
};

const HeatChips = styled.div`
  position: absolute;
  left: ${space[3]};
  top: ${space[3]};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${space[2]};
  pointer-events: none;
  .chip {
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    font-weight: ${weight.semibold};
    padding: ${space[1]} ${inset[3]};
    border-radius: ${radius.pill};
    background: ${rgba(semantic.surface.base, 0.9)};
    color: ${semantic.text.primary};
  }
  .missing {
    font-size: ${typeRole.caption2.fontSize};
    line-height: ${typeRole.caption2.lineHeight};
    padding: ${inset[1]} ${inset[3]};
    border-radius: ${radius.pill};
    /* Missing heat days are information, not a warning (onboarding-status-colours-observers, R12). */
    background: ${semantic.status.roles.information.wash};
    color: ${semantic.status.roles.information.ink};
  }
`;

/**
 * Step 4: define observers against the zones and equipment on the plan.
 *
 * Left: the navigator — Inventory, Hierarchy and Cameras. Right: the stage — the floor plan, an
 * entity's likely cameras or its cameras side by side, or one camera with the drawing area on it
 * (`observers-page-camera-stage`). Nothing opens a pop-up except the Remove Camera confirm.
 */
const OnboardingObservers: React.FC<Props> = ({
  className,
  sessionId,
  onObserverEdit,
  onObserverEditPending,
  registerPendingWriteFlush,
  onNavCollapseWanted,
  onGoToCameraFeeds,
  liveConfiguration,
  noticesSlot = null,
  launchNote,
  planId = null,
  draftChanges,
  draftComparison,
}) => {
  /*
   * The three values this stage reads out of the live configuration.
   *
   * `NO_STREAMS` is a module constant rather than a fresh `[]` so that the memos below keep a
   * stable dependency while the stream read is in flight or has failed.
   */
  const streams =
    liveConfiguration?.streams.status === 'ready' ? liveConfiguration.streams.value : NO_STREAMS;
  const streamsRead: 'loading' | 'ready' | 'failed' =
    liveConfiguration?.streams.status === 'ready'
      ? 'ready'
      : liveConfiguration?.streams.status === 'failed'
        ? 'failed'
        : 'loading';
  /** The twin those rows were read FOR — see `streamsOwned` at the wall mount. */
  const streamsTwinId = liveConfiguration?.streamsTwinId ?? null;

  const reloadSignal = liveConfiguration?.loadNonce ?? 0;
  /*
   * A row with no observers renders no badge at all, so an in-flight read and an entity nothing
   * watches look identical — the feature's own defect, in miniature, in the one place an operator
   * actually reads a count.
   *
   * ⚠ **Not simply `observers.status === 'loading'`.** A failed CONFIGURATION read leaves the
   * observer read permanently at `loading`, because there is no configuration id to read against —
   * so that alone would spin every row for ever on the one path where the counts are never coming.
   * The failure is reported by its own banner instead.
   */
  const countsLoading = liveReadState(liveConfiguration) === 'pending';

  const { getAccessTokenSilently, user } = useAuth0();
  /*
   * The person's heat settings (`onboarding-heatmap-remembered`): the Heatmap and Observers layer
   * switches, heat outlines and their edge, and the heat window. Remembered in their browser on every
   * twin and camera, and shared with the full-screen camera picture in the same tab.
   *
   * - `heatVisible`, the detection heat (`camera-observer-setup-workspace`, FR6/FR7): on to start.
   *   The step owns it and controls the plotter through `heatmapVisible`, because the read needs the
   *   stream BID, the token and the middleware URL, which live here. Live dots are retired
   *   (`observers-heat-follows-tracked-part`, R4): the heat is always the stored history.
   * - `backdropVisible`, other entities' regions on the picture: the *Observers* layer (FR3).
   * - `heatWindow`, the heat HISTORY's window (`observer-autogen-heat-history-view`, R8–R12): 28 days
   *   to start. Its body part is not chosen on its own (`observers-heat-follows-tracked-part`, R1–R3):
   *   it is the part the tracking card shows as chosen, so the heat drawn is the heat the observer
   *   will count. Whole body is HEAD + TORSO + LEGS summed (`heatView.ts`; Mike, 2026-10-22).
   * - `heatOutlinesOn` and the outline edge (`observer-matching-beyond-geometry-heat-outlines`, R1,
   *   R2): off, at 30 % of the hottest heat, to start. The edge is stored when the slider commits.
   */
  const {
    heatOn: heatVisible,
    setHeatOn: setHeatVisible,
    observersOn: backdropVisible,
    setObserversOn: setBackdropVisible,
    outlinesOn: heatOutlinesOn,
    setOutlinesOn: setHeatOutlinesOn,
    outlineEdge: committedOutlineEdge,
    setOutlineEdge: commitOutlineEdge,
    window: heatWindow,
    setWindow: setHeatWindow,
  } = useHeatSettings(user?.sub);
  /** The step's body: the navigator and the stage. Where focus lands when its control has gone. */
  const stageBodyRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [accessToken, setAccessToken] = useState<string>();

  const layout = useFloorplanStore(s => s.layout);
  const storeTwinBID = useFloorplanStore(s => s.twinBID);
  const activeFloorIndex = useFloorplanStore(s => s.activeFloorIndex);
  const setAllFloorObservers = useFloorplanStore(s => s.setAllFloorObservers);

  // Twin/config context for frame capture: the twin id comes from the Setup entry URL, the
  // TwinSense config id is resolved from the twin (with a ?configId= manual override). This is the
  // only stage left reading ?configId= (Camera Feeds' copy went with camera-feeds-page-panel).
  const twinIdParam = Number(searchParams.get('twinId'));
  /** The twin BID from `?twinBid=` — the prefix entity identities fold through, as the page reads it. */
  const twinBidParam = searchParams.get('twinBid') ?? undefined;
  const configIdParam = Number(searchParams.get('configId'));
  // `Number.isInteger`, matching every other twin-id parse in the wizard, the register resolver and
  // the middleware (`wizard-register-on-store`). A fractional `?twinId=` is no twin, everywhere.
  const twinId = Number.isInteger(twinIdParam) && twinIdParam > 0 ? twinIdParam : undefined;
  const configIdOverride =
    Number.isFinite(configIdParam) && configIdParam > 0 ? configIdParam : undefined;
  /*
   * The configuration id, for the MQTT correlation tag on a frame capture.
   *
   * Taken from the page's live-configuration read rather than fetched again. The stage used to
   * issue its own `getTwinSenseConfigs` and swallow the result with `.catch(() => {})` — the silent
   * catch this spec says it deletes outright, left alive in the file it was removed from, and a
   * third fetch of a list the session had already read twice.
   *
   * `?configId=` still wins where it is present: feature 03 kept the override alive as a capture
   * tag that "writes nothing", and this is that use.
   */
  const liveConfigId =
    liveConfiguration?.config.status === 'ready' ? liveConfiguration.config.id : undefined;
  /*
   * Read by nothing since the wall replaced the wizard's own frame capture — the config id was only
   * ever an optional MQTT correlation tag on `captureFrame`, and the stored-frame path does not take
   * one. Kept as a named binding rather than deleted because `configIdOverride` and `liveConfigId`
   * are still the stage's answer to "which configuration is live", which the notices above render.
   */
  void (configIdOverride ?? liveConfigId);

  /**
   * Every one of the twin's feeds, with its picture — the shared camera wall's state machine.
   *
   * ## Why it is mounted HERE and not inside the picker
   *
   * The hook owns the object URLs it creates and revokes them on unmount. The picker lives inside
   * `PolygonPlotter2`'s source dropdown, which unmounts the moment a feed is chosen — so a hook
   * mounted there would revoke the very picture the plotter had just been handed. It is also the
   * one place both consumers can share: the picker and (from the Cameras view) the inventory panel
   * need the same feeds, and two mounts would mean two sets of requests for one twin's cameras.
   *
   * ## Why `endpoints` is passed rather than left for the hook to read
   *
   * `useLiveTwinConfiguration` has already read this twin's streams, and reports that read's own
   * loading/failed state in the notice above this stage. Letting the hook read them again would
   * give one screen two voices for one question — and they can disagree, so an operator could be
   * told "we could not read your camera streams" above a list of camera streams.
   *
   * `twinId` goes to `null` whenever the streams are not ready, which keeps the wall idle rather
   * than asking it to show feeds for a read that has not landed. That also covers the wizard's
   * cross-twin refusal for free: the refusal disables the live read, so `streams` never reaches
   * `ready`, so no frame is ever fetched for a twin the wizard declined to open.
   */
  /*
   * `error` is deliberately not taken. It can only be set by the hook's OWN stream read, and this
   * caller supplies `endpoints` so that read never happens — the stream failure is reported once,
   * by `LiveConfigurationNotices` above the stage and by the picker's own banner, which is the
   * single voice this arrangement exists to preserve.
   */
  /*
   * ⚠ `streamsOwned` — the stream rows were read FOR the twin currently selected.
   *
   * `streamsRead === 'ready'` is not that question. The read's effect sets `loading` after commit,
   * so on the render where `twinId` changes the rows are still the PREVIOUS twin's while the id is
   * already the new one — and handing those two to the wall together seeds one customer's endpoint
   * ids under another customer's twin, and requests frames for them. The hook now stamps the twin
   * each value was read for so this can be asked rather than assumed.
   */
  const streamsOwned = streamsRead === 'ready' && twinId != null && streamsTwinId === twinId;

  const { feeds, refreshFeed } = useCameraWallFeeds({
    baseUrl: Config.middlewareApi,
    accessToken,
    twinId: streamsOwned ? twinId : null,
    endpoints: streamsOwned ? streams : undefined,
  });

  /**
   * What the two feed surfaces should SAY about the read — which is not the same question as
   * whether the stream list arrived.
   *
   * ⚠ The wall seeds in an effect, so on the render where the streams flip to `ready` its `feeds`
   * is still empty. Reporting `ready` there paints "This twin has no camera feeds" on a gym with
   * fourteen of them — the affirmative false claim this whole feature exists to remove, arriving
   * one render early. The same guard covers the permanent version: if the access token never lands
   * the wall never seeds, and waiting is the honest answer rather than claiming the twin has no
   * cameras. `LiveConfigurationNotices` above the stage carries the retry either way.
   */
  const streamsStatus: 'loading' | 'ready' | 'failed' =
    streamsRead !== 'ready'
      ? streamsRead
      : streams.length > 0 && feeds.length === 0
        ? 'loading'
        : 'ready';

  /** The wall's picture for one endpoint, or nothing. One lookup, three readers. */
  const feedById = useMemo(() => new Map(feeds.map(f => [Number(f.id), f])), [feeds]);

  useEffect(() => {
    let cancelled = false;
    void getAccessTokenSilently().then(token => {
      if (!cancelled) setAccessToken(token);
    });
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  const design = useMemo(
    () =>
      layout && storeTwinBID === sessionId
        ? findLatestDesign(layout.floors[activeFloorIndex]?.designs ?? [])
        : null,
    [layout, storeTwinBID, sessionId, activeFloorIndex],
  );

  const surfaces = useMemo(() => (design?.surfaces ?? []).filter(s => !s._deleted), [design]);
  const items = useMemo(() => (design?.items ?? []).filter(i => !i._deleted), [design]);
  const cameras = useMemo(() => (design?.cameras ?? []).filter(c => !c._deleted), [design]);

  // Index of every entity across ALL floors → its floor + display metadata.
  // The canvas and inventory panel above show one floor at a time, but observer
  // editing state is keyed by the globally-unique entity refid, so saving and
  // hydrating span floors: this resolves which floor each drawn observer belongs
  // to (and that floor's cameras for the RTSP-bid link).
  /*
   * `name` is gone from this index (`plan-item-register-sync`, ledger row 61). Nothing read it once
   * `buildObserversByFloor` stopped snapshotting the target's name into the observer — and leaving a
   * draw-time copy of a name the register can change would be the very thing that row deletes. The
   * one definition of an entity's CURRENT display name is `entityNamesByRefid`, resolved at the
   * moment it is used.
   */
  const floorEntityIndex = useMemo(
    () => buildFloorEntityIndex(layout && storeTwinBID === sessionId ? layout : null),
    [layout, storeTwinBID, sessionId],
  );

  // Observer polygons + the camera frame they're drawn on, keyed by entity refid.
  const [polygonsByEntity, setPolygonsByEntity] = useState<Record<string, Polygon[]>>({});
  /**
   * Per-OBSERVER camera and track side, keyed by the observer's own refid.
   *
   * Replaces `sourceByEntity` and `trackSideByEntity`, which were keyed by entity and so could hold
   * one camera and one track side per piece of equipment. The domain does not work that way — an
   * observer watches one camera, and an entity can be watched by several (Rob, 2026-09-08) — and
   * neither does the data: 106 production entities across 10 twins are watched through more than
   * one camera, and 23 carry observers that disagree on track side.
   */
  const [observerState, setObserverState] = useState<ObserverStateByRefid>({});
  /**
   * The camera chosen for an entity that has no observer yet — the drawing surface for the FIRST
   * observer on it, until the polygon that will own the link exists. Cleared on every entity open.
   */
  const [pendingEndpointId, setPendingEndpointId] = useState<number | null>(null);
  /** Bumped on every way INTO the drawing area, so each entry is seeded afresh (see `initialPolygons`). */
  const [cameraEntry, setCameraEntry] = useState(0);
  const [selected, setSelected] = useState<SelectedEntity | null>(null);
  /**
   * What the stage shows (`observers-page-stage`, FR18), and the entity picked there — separate from
   * `selected`, which is the entity the EDITOR is open on. Picking puts the plan's ring and the
   * list's highlight on an entity without opening anything.
   */
  const [stage, setStage] = useState<StageView>(PLAN_VIEW);
  const [stagePick, setStagePick] = useState<SelectedEntity | null>(null);
  /** The plan's hover, in a store only the stage and the list's rows read (never step state). */
  const [hoverStore] = useState(createHoverStore);
  /** Edit's hover on the picture (FR20): the hovered region's entity, for its list row only. */
  const [pictureHoverStore] = useState(createHoverStore);
  /** Bumped by *Recentre* and R. */
  const [recentreSignal, setRecentreSignal] = useState(0);
  /** *Remove Camera* waiting on its confirm: which entity, which camera (`null`: the unlinked group). */
  const [removeTarget, setRemoveTarget] = useState<{
    entity: SelectedEntity;
    endpointId: number | null;
  } | null>(null);
  /** Whatever put the camera on stage, so leaving it returns focus there (FR23). */
  const editorOpenerRef = useRef<HTMLElement | null>(null);
  /**
   * The camera-first workspace (`camera-first-observers`): `null` = not open; `{ endpointId: null }`
   * = choosing a camera; a number = the camera it is fixed to. It and the entity editor are ONE
   * editor with two entry points — each fixes one axis. Here the camera is fixed and the entity
   * moves; in the entity editor it is the other way round.
   */
  const [workspace, setWorkspace] = useState<{ endpointId: number | null } | null>(null);
  /**
   * The one region drawn in the workspace with nothing picked, waiting for a pick that links it
   * (R7). Never in the plan, never marks it unsaved, and thrown away by *Change camera*, closing or
   * Save. Mirrored OUT of the plotter, never seeded back in: every way into "nothing picked" starts
   * with the plotter reseeded empty.
   */
  const [waitingRegion, setWaitingRegion] = useState<Polygon | null>(null);
  /**
   * One camera is on the stage — either entry point: an entity's camera (`selected`), or a camera
   * with nothing picked yet (`workspace`, camera by camera). `observers-page-camera-stage`, FR1.
   */
  const editorOpen = !!workspace || !!selected;
  /** Where the camera was opened from: Esc, *Side by Side* and focus go back there (FR18, FR23). */
  const [cameraFrom, setCameraFrom] = useState<StageView>(PLAN_VIEW);
  /*
   * *Add Observer* has armed one region (`observer-unified-draw-edit`, R4): the next unmoved click on
   * the picture starts it. There is no mode: a plain click selects, Shift+click draws. Disarmed by
   * the first point, Esc, the button again, and every camera entry, entity change and leave (below).
   */
  const [addObserverArmed, setAddObserverArmed] = useState(false);
  /** The shortcut card above the dock (FR12), closed by default. */
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const shortcutsId = useId();
  /** Points in the region being drawn, as the drawing area reports them — the Undo bar (FR5). */
  const [drawingPoints, setDrawingPoints] = useState(0);
  /** The heat's tools panel — *Heat Tools* (FR3). Closed on every camera change. */
  /** The picture's zoom, for the dock's readout (FR11) — a store, so a wheel tick re-renders only it. */
  const [pictureZoom] = useState(() => createZoomReadout());
  /** What the next region drawn tracks, while no observer is selected (FR10). */
  const [newRegionTrackSide, setNewRegionTrackSide] =
    useState<ObserverTrackSide>(NEW_OBSERVER_TRACK_SIDE);
  /** A region of the picked entity has closed since it came on this camera (the step guide). */
  const [regionClosedHere, setRegionClosedHere] = useState(false);
  const plotterRef = useRef<PolygonPlotterHandle>(null);
  /**
   * Which of the open entity's observers is being edited.
   *
   * New with the per-observer model: an entity can carry several observers on several cameras, so
   * "the frame for this entity" stopped being a question with one answer. Null means the entity is
   * open with none of its observers selected — where a fresh draw starts.
   */
  const [selectedObserverId, setSelectedObserverId] = useState<string | null>(null);

  const observerCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [refid, polys] of Object.entries(polygonsByEntity)) {
      counts[refid] = polys.filter(p => p.isComplete).length;
    }
    return counts;
  }, [polygonsByEntity]);

  /*
   * The four attention states (`camera-observer-setup-attention`), one memo, one call. The
   * classifier is pure; everything it reads is already session state here, plus what the draft
   * added against production (the page's change source) and the running model the page read.
   */
  const addedRefids = useMemo(
    () => addedRefidsFrom(draftChanges, draftComparison),
    [draftChanges, draftComparison],
  );
  const stamps = useObserverStampStore(s => s.stamps);
  const stampObservers = useObserverStampStore(s => s.stampObservers);
  const sessionLayout = layout && storeTwinBID === sessionId ? layout : null;
  const model = liveConfiguration?.model;
  // The expensive half — the running model against the whole plan — keyed only on what it reads,
  // so dragging a vertex (which changes `polygonsByEntity` on every pointer move) never re-diffs
  // the twin.
  const modelChanges = useMemo(
    () => computeModelChanges({ model, layout: sessionLayout, twinBid: twinBidParam }),
    [model, sessionLayout, twinBidParam],
  );

  /*
   * The title-bar search's index (`onboarding-polish-search`, FR5): every area and machine on every
   * floor — the navigator lists only the active floor — plus one entry per camera FEED, the things
   * the navigator's Cameras view lists and opens (a twin can have feeds and no placed cameras at
   * all). The same matcher as the Floor plan, over the same register and catalogue facts.
   */
  /*
   * The feeds' id and name — all the search reads of a feed. The wall hands `feeds` a new identity
   * on every picture refresh and staleness tick, which must not rebuild the whole plan index on a
   * stage built to be left open (found by review).
   */
  const feedKey = JSON.stringify(feeds.map(f => [f.id, f.name]));
  const searchFeeds = useMemo(
    () => feeds.map(f => ({ id: Number(f.id), name: f.name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on what the search reads
    [feedKey],
  );
  const { feedEntries, feedIdByRefid } = useMemo(() => {
    const ids = new Map<string, number>();
    const entries = searchFeeds.map(feed => {
      const refid = `feed-${feed.id}`;
      ids.set(refid, feed.id);
      const floorIndex = feedSight(sessionLayout, feed.id)?.floorIndex ?? null;
      const floorName =
        floorIndex != null ? sessionLayout?.floors[floorIndex]?.name?.trim() || null : null;
      return planSearchEntry({
        refid,
        kind: 'feed',
        floorIndex,
        floorName,
        label: feed.name,
        code: '',
        detail: '',
        searchable: [feed.name],
      });
    });
    return { feedEntries: entries, feedIdByRefid: ids };
  }, [searchFeeds, sessionLayout]);
  // The plan joined to the register, as the Floor plan searches it (`useJoinedPlanSearchIndex`).
  const planSearch = useJoinedPlanSearchIndex(sessionLayout, {
    twinBid: twinBidParam,
    twinId,
    include: OBSERVER_SEARCH_KINDS,
    extra: feedEntries,
  });
  const searchIndex = planSearch.index;
  /**
   * What measures each thing on this plan — the ONE rule, shared with the Launch checklist and the
   * complex-measures editor (`entityCoverage.ts`, epic ledger row 82).
   *
   * ⚠ The observer counts come from the SESSION's polygons, not from the saved plan. A region the
   * operator drew thirty seconds ago and has not saved still measures its entity, and this stage is
   * where that happens. `entityCoverage` takes the counts as a parameter precisely so this surface
   * and the Launch checklist — which reads the saved plan, because that is what Go Live files — can
   * share one rule while answering from different sources.
   *
   * Memoised apart from the model diff above, for the reason that split exists: this recomputes
   * when a polygon is completed, which is often; that one walks the whole running twin.
   */
  /*
   * The layout-only half, keyed on the layout alone — NOT on the polygons.
   *
   * ⚠ This split is the whole of NFR1 and the first version did not have it. `polygonsByEntity` is
   * replaced on every pointer move of a vertex drag (see the comment above `modelChanges`), so a
   * single memo over both walked all 160 plan entities, rebuilt their display names and rebuilt
   * every encoded reference on every frame — the exact per-frame cost the requirement forbids, on
   * data that cannot change unless the plan does.
   */
  const planEntityList = useMemo(() => planEntities(sessionLayout), [sessionLayout]);
  const planMeasureList = useMemo(() => planMeasures(sessionLayout?.composites), [sessionLayout]);

  const coverage = useMemo(() => {
    const counts = new Map<string, number>();
    for (const entity of planEntityList) {
      // `observerCounts` above already counted the completed polygons per entity, from the same
      // source. Re-filtering here would be a second answer to "how many observers does this have",
      // which is the duplication this whole feature exists to remove.
      const complete = observerCounts[entity.refid] ?? 0;
      if (complete > 0) counts.set(entity.ref, complete);
    }
    return computeCoverage({ observerCountsByRef: counts, measures: planMeasureList });
  }, [planEntityList, planMeasureList, observerCounts]);

  const attentionByRefid = useMemo(
    () =>
      classifyEntityAttention({
        layout: sessionLayout,
        addedRefids,
        polygonsByEntity,
        stamps,
        modelChanges,
        coverage,
        countsLoading,
      }),
    [sessionLayout, addedRefids, polygonsByEntity, stamps, modelChanges, coverage, countsLoading],
  );
  /**
   * How the observers read went, for the panel: a failed configuration read also means no counts.
   *
   * ⚠ Lifted into `entityCoverage.ts` so the Launch checklist reads the SAME answer. Two copies of
   * "has the twin's own configuration landed?" is how one surface comes to show a coverage figure
   * while the other is still waiting for it.
   */
  const observersStatus = liveReadState(liveConfiguration);
  const modelStatus = liveConfiguration?.model?.status ?? 'none';
  /**
   * FR12: the three attention states counted across the twin, split by the active floor, and how
   * many of them need checking, which turns the strip from work to do to a problem (R7).
   */
  const attentionSummary = useMemo(() => {
    let thisFloor = 0;
    let otherFloors = 0;
    let checking = 0;
    for (const [refid, a] of Object.entries(attentionByRefid)) {
      if (!NEEDS_ATTENTION.has(a.state)) continue;
      if (a.state === 'check') checking += 1;
      if (floorEntityIndex.get(refid)?.floorIndex === activeFloorIndex) thisFloor += 1;
      else otherFloors += 1;
    }
    return { thisFloor, otherFloors, checking };
  }, [attentionByRefid, floorEntityIndex, activeFloorIndex]);

  /**
   * The plan's observer records, rebuilt from the session.
   *
   * The work moved to `observerSessionState.ts` so it can be tested without rendering a component
   * that pulls the planner canvas and the whole `@repo/ui` barrel into jsdom — and so the
   * per-observer keying it now depends on is pinned where a fixture can drive it.
   */
  const buildObservers = (): FloorPlannerObserver[][] =>
    buildObserversByFloor(
      polygonsByEntity,
      observerState,
      floorEntityIndex,
      layout?.floors.length ?? 0,
    );

  /**
   * Write each floor's observers into its own design. The ONE write both routes share — every edit
   * (the effect below) and the editor's own button — so the two cannot come to disagree about what
   * the plan holds.
   */
  const writeObserversIntoPlan = () => setAllFloorObservers(buildObservers());

  // Hydrate saved observers (from a reloaded plan) into the editor state once,
  // when there's nothing drawn in-session yet. The frame image isn't stored, so
  // only the polygon + camera link are restored; the frame re-loads when the
  // user reopens the entity and picks the camera again.
  const hydratedRef = useRef(false);
  /** The write-through's waiting animation frame (see the edit-counter effect), or `null`. */
  const writeFrameRef = useRef<number | null>(null);
  // A fresh load supersedes whatever is held here (FR10), so release the latch and let the effect
  // below re-read the store. Ignored on first mount (0), like every other signal on this stage.
  const lastReloadRef = useRef(0);
  if (reloadSignal !== lastReloadRef.current) {
    lastReloadRef.current = reloadSignal;
    if (reloadSignal) hydratedRef.current = false;
  }
  useEffect(() => {
    if (hydratedRef.current) return;
    // An edit is waiting for its write-through frame: the store does not hold it yet, so reading the
    // store now would put the pre-edit regions back over it. The write changes `layout`, which
    // re-runs this effect with the edit in the store.
    if (writeFrameRef.current != null) return;
    if (reloadSignal === 0 && Object.keys(polygonsByEntity).length > 0) {
      hydratedRef.current = true; // in-session edits exist — don't clobber them
      return;
    }
    // Hydrate from every floor's saved observers (state is keyed by the
    // globally-unique refid, so a single merge across floors is correct).
    const session = hydrateObserverSession(layout ?? null);
    /*
     * Nothing saved yet. Before any load, wait for one. After a load, an empty plan IS the answer
     * and latches like any other: left unlatched, the operator's own first edit would come back
     * through the store as a "load" and clear the undo history it had just recorded.
     */
    if (Object.keys(session.polygonsByEntity).length === 0 && reloadSignal === 0) return;
    hydratedRef.current = true;
    // A load is not the operator's work: an undo step taken before it would put back what it replaced.
    clearHistory();
    setPolygonsByEntity(session.polygonsByEntity);
    setObserverState(session.observerState);
  }, [layout, polygonsByEntity, reloadSignal]);

  /**
   * Every entity on the TWIN, by refid, with its display name.
   *
   * The panel lists one floor at a time and reads `surfaces`/`items` directly; the region tips and
   * the floor row name entities across the whole twin, so they read from here instead — a region
   * whose entity sits on another floor would otherwise render as its raw refid.
   */
  const entityLabels = useMemo(() => {
    const map = new Map<string, { label: string; code?: string }>();
    for (const design of layoutCurrentDesigns(layout)) {
      for (const surface of asArray(design?.surfaces).filter(isLive)) {
        map.set(surface.refid, {
          // Name first, as the plan reads it (`onboarding-polish-plan-labels`; epic ledger row 27).
          label: surfaceName(surface) || 'Untitled zone',
          code: surface.entityCode,
        });
      }
      for (const item of asArray(design?.items).filter(isLive)) {
        map.set(item.refid, { label: item.name || 'Equipment', code: item.entityCode });
      }
    }
    /*
     * Floors too — a floor can be an observer's target, and the editor's title reads from here.
     *
     * ⚠ From `sessionLayout`, the SAME twin guard the zones and machines pass through: floor ids
     * are small integers repeated across twins, so a floor read off the previous twin's layout
     * would key a region drawn now to whatever the new twin's floor of that id turns out to be.
     * ⚠ Only a FILEABLE floor — named, and not sharing a folded name with another floor
     * (`fileableFloorBids`): a region drawn on any other floor could never be filed and would vanish
     * at Go Live with nothing said.
     */
    const fileable = fileableFloorBids(sessionLayout, storeTwinBID);
    for (const floor of asArray(sessionLayout?.floors)) {
      if (typeof floor?.id !== 'number') continue;
      const code = fileable.get(floor.id);
      if (!code) continue;
      map.set(floorTargetRefid(floor.id), { label: floor.name.trim(), code });
    }
    return map;
  }, [layout, sessionLayout, storeTwinBID]);

  /**
   * What the workspace's camera sees on the plan (`camera-first-observers`, R13) — once per camera
   * and layout, never on `polygonsByEntity`, which changes on every pointer move of a drag (NFR1).
   * `null`: no camera on the plan links to the feed, so its lists show everything.
   */
  const workspaceEndpointId = workspace?.endpointId ?? null;
  const workspaceSight = useMemo(
    () => (workspaceEndpointId != null ? feedSight(sessionLayout, workspaceEndpointId) : null),
    [workspaceEndpointId, sessionLayout],
  );

  /** The active floor, as a target the panel lists above its zones and machines. */
  const activeFloorTarget = useMemo(() => {
    const floor = asArray(sessionLayout?.floors)[activeFloorIndex];
    if (typeof floor?.id !== 'number') return undefined;
    const refid = floorTargetRefid(floor.id);
    const entry = entityLabels.get(refid);
    return entry ? { refid, name: entry.label } : undefined;
  }, [sessionLayout, activeFloorIndex, entityLabels]);

  /**
   * How many of this session's observers sit on each feed. Derived, never a new read.
   *
   * A map built in ONE pass, not a predicate called once per feed: the Cameras view asks for every
   * feed on every render, and at the measured maxima — 46 feeds, 241 observers — a per-feed filter
   * is ~11,000 comparisons and 46 intermediate arrays each time the panel re-renders, which it does
   * once per arriving picture and once a minute on the wall's staleness tick.
   */
  /*
   * ⚠ Counted from the finished REGIONS joined to their state, not from the state entries alone.
   * `handlePolygonChange` drops only the SELECTED observer's entry when regions go, so after
   * *Clear Observer* on a camera with two regions an orphan entry remained — and the count said
   * "1 observer" on a camera carrying none. Two new readers depend on it being right: the
   * workspace's *Save* / *Save changes* label (R11) and the Cameras view's accessible names (R2).
   */
  const observersByFeed = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const [refid, polys] of Object.entries(polygonsByEntity)) {
      for (const p of polys) {
        const endpointId = endpointOfRegion(refid, p, observerState);
        if (endpointId != null) counts[endpointId] = (counts[endpointId] ?? 0) + 1;
      }
    }
    return counts;
  }, [polygonsByEntity, observerState]);

  /**
   * Each entity's complete regions on the WORKSPACE's camera (`workspace-inventory-this-camera`):
   * what its lists add beyond the cone, and each row's "{n} on this camera". Like `observersByFeed`,
   * from the live session, so a region linked a moment ago counts. The panel memoises its listed set
   * on membership, so a vertex drag — which rebuilds this record — does not re-prune its tree.
   */
  const observersOnThisCamera = useMemo(
    () =>
      workspaceEndpointId == null
        ? undefined
        : regionsOnFeed(polygonsByEntity, observerState, workspaceEndpointId),
    [workspaceEndpointId, polygonsByEntity, observerState],
  );

  /*
   * Each camera's observer regions, for its tile's hover zoom (`camera-zoom-dwell-and-observers`,
   * FR7) — from the LIVE session, unsaved edits included, so the zoom shows what the operator would
   * file. `polygonsByEntity` changes on every pointer move of a drag; `zoomRegionsByCamera` hands
   * every camera but the edited one its previous array, so only that camera's tile re-renders.
   */
  const zoomRegionsByFeed = useZoomRegionsByCamera(polygonsByEntity, observerState);

  // Frame chosen for the open entity, and the camera it came from — the camera
  // carries the RTSP feed's BID (cached when the feed was linked), which the
  // heatmap fetch keys on.
  /**
   * The camera the plotter is drawing on.
   *
   * Normally the OPEN OBSERVER's own — not the entity's, which no longer means anything. But an
   * entity with no observers yet has no observer to carry one, and that is the path on which a NEW
   * observer is drawn.
   *
   * ⚠ **Without `pendingEndpointId` this is a closed loop, and it is the defect this feature very
   * nearly shipped.** `openEditor` selects `null` on an entity with no completed polygon; picking a
   * camera then had nowhere to record the link, so `selectedEndpointId` stayed `null`, the plotter
   * got no image, the operator could not draw, and no observer was ever created to select. The
   * per-entity model this replaced stored the camera against the ENTITY, so it never had the gap.
   */
  /*
   * ⚠ In the camera workspace the fixed camera IS the drawing surface, whatever the picked entity's
   * observers use, and it is tested FIRST: that is the whole of "picking never changes the camera"
   * (`camera-first-observers`, R6). `pendingEndpointId` is not consulted there.
   */
  const selectedEndpointId = workspace
    ? workspace.endpointId
    : selectedObserverId
      ? (observerState[selectedObserverId]?.endpointId ?? null)
      : pendingEndpointId;

  /**
   * The picture the plotter draws on: whatever the wall currently holds for the open observer's
   * feed.
   *
   * ⚠ **Not a copy, and that is deliberate.** This used to be a data URL kept in a session map and
   * a second one in the wizard's own frame store. The wall owns its object URLs and revokes them
   * on replacement, so copying one here would leave the plotter holding a revoked `blob:` after a
   * refresh. Reading through means a per-feed refresh updates the picture UNDER a region being
   * drawn — which is correct: observer polygons are held in the frame's natural pixel coordinates
   * and Go Live re-captures the frame at activation regardless, so the region is unaffected by the
   * picture beneath it changing.
   */
  const selectedFeed = selectedEndpointId != null ? feedById.get(selectedEndpointId) : undefined;
  const effectiveFrameDataUrl = selectedFeed?.imageUrl ?? null;

  /**
   * Why the open observer has no picture, when the reason is a failure rather than a wait.
   *
   * `null` while it is genuinely still loading, or once a frame has arrived — the plotter's own
   * "Awaiting camera feed" is the right thing to say then, and only then.
   *
   * The two states that are NOT a wait are distinct and stay distinct. `not_configured` is the
   * feed having nothing to ask — the server's own reason, e.g. "RTSP endpoint 198 has no edge
   * device attached; cannot request a frame capture" — and `error` is the ask having failed, with
   * the wall's classification (Session expired / Service error / Request failed (n) / Signal lost).
   * Collapsing them would tell an operator their camera is broken when nobody ever asked it.
   */
  const frameFailure =
    selectedFeed && !selectedFeed.imageUrl
      ? selectedFeed.state === 'not_configured'
        ? /*
           * Sentence-cased and full-stopped, because the server's reason is neither and it is
           * rendered as a sentence of its own beneath a title that ends in a full stop. Measured on
           * staging twin 522: "no edge device is attached to this RTSP stream" arrives lowercase
           * and unpunctuated, and read straight on from the sentence before it.
           */
          sentence(selectedFeed.reason ?? '', 'This feed has no camera set up yet')
        : selectedFeed.state === 'error'
          ? sentence(selectedFeed.statusLabel ?? '', 'The camera did not return a frame')
          : null
      : null;

  // Other entities' observers on the camera being SHOWN, as a read-only backdrop so the user can
  // place a region relative to the ones already set up. Keyed on the camera rather than the selected
  // observer, so a first region drawn on a just-picked camera has its backdrop too; and never the
  // open entity's own regions, which the plotter already holds as editable
  // (`multi-camera-observer-view`, R6). Derived entirely from local state — no refetch.
  const otherObserverPolygons = useMemo(
    () =>
      backdropPolygonsOnCamera(
        selectedEndpointId,
        selected?.refid,
        polygonsByEntity,
        observerState,
      ),
    [selectedEndpointId, selected?.refid, observerState, polygonsByEntity],
  );

  /*
   * What the hover tip says about each region on the picture (row 129): the name Go Live will file
   * it under, and the body part it tracks — from the same session state the Track control reads,
   * so the two cannot disagree.
   *
   * Keyed two ways, because the plotter keys its two kinds of region two ways: the open entity's own
   * polygons by their plain polygon id, and the backdrop (`backdropPolygonsOnCamera`) by
   * the observer refid. Every observer is keyed by its refid, so any backdrop region is covered.
   *
   * The stored camera number comes from the READY statement only. While it loads, or when it failed,
   * loaded observers can still be on the plan — a saved plan keeps their `:live-` refids — so their
   * numbers are simply unknown: `observerTip` gives them their bare name (`loaded`), never "new". A
   * drawn observer is never numbered by the browser, whatever the statement says.
   */
  const liveStatement =
    liveConfiguration?.statement.status === 'ready' ? liveConfiguration.statement.value : null;
  // What the tip reads that does not change as the operator draws — held apart from the per-edit
  // work below, so a point drag does not re-walk the layout and the statement on every frame.
  const targetNames = useMemo(() => observerTargetNamesByRefid(layout), [layout]);
  const storedOrdinals = useMemo(() => storedOrdinalById(liveStatement), [liveStatement]);
  const statementObserverIds = useMemo(
    () => new Set(asArray(liveStatement?.observers).map(o => (o as { id?: unknown })?.id)),
    [liveStatement],
  );
  /*
   * What one observer is called and what it tracks — the ONE producer of that text. The region hover
   * tip, the observer strip and the stage's side-by-side captions all read it, so a person reading
   * any of them sees the name Go Live will file (`multi-camera-observer-view`, R4 and R6).
   */
  const tipForObserver = useCallback(
    (targetRefid: string, polygonId: string, siblingCount: number): ObserverTip => {
      const refid = observerRefid(targetRefid, polygonId);
      const liveId = liveObserverId(refid);
      const label = entityLabels.get(targetRefid);
      return observerTip({
        entityName: targetNames.get(targetRefid),
        fallbackName: label?.code || label?.label || targetRefid,
        loaded: liveId !== null,
        storedOrdinal: liveId !== null ? storedOrdinals.get(liveId) : undefined,
        siblingCount,
        trackSide: observerState[refid]?.trackSide,
      });
    },
    [entityLabels, targetNames, storedOrdinals, observerState],
  );
  const { polygonTips, unmatchedLiveRefids } = useMemo(() => {
    const tips: Record<string, PolygonTip> = {};
    const unmatched: string[] = [];
    // Open with nothing picked (the camera workspace): every region on the camera is backdrop, and
    // every one still has its tip (R5).
    if (!editorOpen) return { polygonTips: tips, unmatchedLiveRefids: unmatched };
    // Only the regions the picture can show: the open entity's own, and the backdrop.
    const backdrop = new Set(otherObserverPolygons.map(p => p.id));
    for (const [targetRefid, polys] of Object.entries(polygonsByEntity)) {
      const complete = polys.filter(p => p.isComplete);
      const isOpen = targetRefid === selected?.refid;
      for (const polygon of complete) {
        const refid = observerRefid(targetRefid, polygon.id);
        if (!isOpen && !backdrop.has(refid)) continue;
        const liveId = liveObserverId(refid);
        if (liveId !== null && liveStatement && !statementObserverIds.has(liveId)) {
          unmatched.push(refid);
        }
        const { title, detail } = tipForObserver(targetRefid, polygon.id, complete.length);
        // The plotter's tip is the two lines; the number and "new" facts are the side-by-side captions'.
        const tip: PolygonTip = { title, detail, code: entityLabels.get(targetRefid)?.code };
        tips[refid] = tip;
        if (isOpen) tips[polygon.id] = tip;
      }
    }
    return { polygonTips: tips, unmatchedLiveRefids: unmatched };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `entityLabels` already changes `tipForObserver`, which is listed
  }, [
    editorOpen,
    selected,
    otherObserverPolygons,
    polygonsByEntity,
    liveStatement,
    statementObserverIds,
    tipForObserver,
  ]);

  /*
   * A loaded observer the statement no longer holds means the plan and the configuration disagree
   * (Failure States, row 3). The tip still reads the bare name — it never guesses — and this says
   * so once per editor open rather than once per render.
   */
  useEffect(() => {
    if (!editorOpen || unmatchedLiveRefids.length === 0) return;
    console.warn(
      `Observer editor: ${unmatchedLiveRefids.length} loaded observer(s) are not in the live ` +
        `configuration statement, so their camera numbers cannot be shown: ` +
        unmatchedLiveRefids.join(', '),
    );
    // Once per open, and once more if the statement arrives after it: re-running on every edit would
    // repeat the same sentence as the operator draws.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.refid, workspace?.endpointId, liveStatement]);

  const selectedRtspBid = useMemo(
    () =>
      selectedEndpointId == null
        ? null
        : /*
           * The STREAM's BID, from the twin's stream list first and the plan's cameras second.
           *
           * Plan cameras alone are not enough any more: a loaded observer can name a stream that
           * nothing on the plan points at — `NP2-STN` carries 151 observers and zero placed
           * cameras — and reading only `cameras` left the heatmap silently unavailable on exactly
           * the twins this feature exists to open.
           */
          (streams.find(e => e.id === selectedEndpointId)?.bid ??
          cameras.find(c => c.rtspEndpointId === selectedEndpointId)?.rtspEndpointBid ??
          null),
    [cameras, streams, selectedEndpointId],
  );

  /*
   * Seed the plotter with the open entity's regions ON THE CAMERA SHOWN — never the whole entity.
   *
   * ⚠ Seeding the whole entity was the defect `multi-camera-observer-view` removes: the plotter keeps
   * every region across a source change, so an entity watched from
   * cameras 97 and 99 had its camera-99 region drawn over camera 97's picture, where it means nothing.
   *
   * The reference is kept STABLE for the whole time one camera is shown. The plotter reloads —
   * ending any drag and clearing point selection — whenever `initialPolygonSet`'s identity changes,
   * and it echoes our own edits straight back via onPolygonChange. So binding it to the live
   * `polygonsByEntity` would re-fire that reset on every drag mouse-move (ending the drag and
   * deselecting the point). Recompute only when the entity or the camera changes, or the drawing
   * area is ENTERED again (`cameraEntry`); the plotter is keyed by the camera alone, so an entity
   * switch on the same camera reseeds it in place (keeping its zoom and pan) and only a camera switch
   * remounts it.
   *
   * ⚠ `cameraEntry` is not decoration, and it is the defect review found (2026-09-25). The plotter is
   * unmounted while the cameras are side by side, so coming back to the SAME camera remounts it — and with
   * neither the entity nor the camera changed, the memo handed back the array from the FIRST visit.
   * The plotter echoed that stale seed, and the merge wrote it over everything done since: an edit
   * reverted, a region drawn this session dropped, a deleted one brought back, all filed at Save.
   * Every way into the drawing area bumps it (`openEditor`, `editCamera`, `addCamera`).
   *
   * The plotter's seed effect echoes EXACTLY this array back through `onPolygonChange`, which is how
   * `handlePolygonChange` tells a seed from an edit: it compares against `initialPolygons` from the
   * same render. (A ref assigned in this memo was tried and removed — a memo body runs twice under
   * Strict Mode and React keeps the first result, so the ref pointed at an array the plotter never
   * saw, and merely looking at a camera marked the plan dirty.)
   */
  const initialPolygons = useMemo(
    () =>
      selected
        ? polygonsOnCamera(
            selected.refid,
            polygonsByEntity[selected.refid] ?? [],
            observerState,
            selectedEndpointId,
          )
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected?.refid, selectedEndpointId, cameraEntry],
  );
  /*
   * *Add Observer*'s armed region belongs to this entity on this camera (`observer-unified-draw-edit`,
   * R4.3): picking another entity, changing camera, a fresh entry onto the picture or leaving it
   * disarms it.
   */
  useEffect(() => {
    setAddObserverArmed(false);
  }, [selected?.refid, selectedEndpointId, cameraEntry, editorOpen]);

  // The outline edge while the slider is dragged: drawn live, stored only when the slider commits
  // (`onboarding-heatmap-remembered`, R7).
  const [draggingOutlineEdge, setDraggingOutlineEdge] = useState<number | null>(null);
  const outlineEdge = draggingOutlineEdge ?? committedOutlineEdge;
  const [outlineEdgeCommits, setOutlineEdgeCommits] = useState(0);
  // *Separate peaks* (`observer-matching-beyond-geometry-region-split`, R3): on to start, for the
  // session; a change is said like an edge commit (R4).
  const [separatePeaks, setSeparatePeaks] = useState(true);
  const heatTrackSide: ObserverTrackSide = selectedObserverId
    ? (observerState[selectedObserverId]?.trackSide ?? DEFAULT_TRACK_SIDE)
    : newRegionTrackSide;
  const heatView: HeatView = heatViewForTrackSide(heatTrackSide);
  const [grid, setGrid] = useState<{ key: string | null; state: GridHeatReadState }>({
    key: null,
    state: 'idle',
  });
  /**
   * Ready answers by `{rtspBid}|{window}|{view}`, for the life of the step (R11). Failures are not
   * kept. A one-part view's key is its part's, so a whole-body read also fills Head, Torso and Legs.
   */
  const gridCacheRef = useRef(new Map<string, HeatGridOutcome>());
  const gridRequestIdRef = useRef(0);
  const gridInFlightRef = useRef<{ controller: AbortController; timer: number } | null>(null);
  const abortGridRead = () => {
    const inFlight = gridInFlightRef.current;
    if (!inFlight) return;
    window.clearTimeout(inFlight.timer);
    inFlight.controller.abort();
    gridInFlightRef.current = null;
  };
  useEffect(() => () => abortGridRead(), []);

  const readGridHeat = useCallback(
    (rtspBid: string | null, token: string | undefined, win: HeatGridWindow, view: HeatView) => {
      gridRequestIdRef.current += 1;
      abortGridRead();
      if (!rtspBid || !token) {
        setGrid({ key: null, state: 'idle' });
        return;
      }
      const keyOf = (v: HeatView) => `${rtspBid}|${win}|${v}`;
      const key = keyOf(view);
      const cached = gridCacheRef.current.get(key);
      if (cached) {
        setGrid({ key, state: cached });
        return;
      }
      const requestId = gridRequestIdRef.current;
      // The AbortSignal is the wall-clock deadline, axios's `timeout` inside the read the guard on
      // a stalled connect. A whole body reads each of its parts not already kept, under the one
      // signal and the one deadline, so it settles once.
      const controller = new AbortController();
      let deadlinePassed = false;
      const timer = window.setTimeout(() => {
        deadlinePassed = true;
        controller.abort();
      }, MIDDLEWARE_REQUEST_TIMEOUT_MS);
      gridInFlightRef.current = { controller, timer };
      setGrid({ key, state: 'loading' });
      const parts = HEAT_VIEW_PARTS[view];
      Promise.all(
        parts.map(part => {
          const kept = gridCacheRef.current.get(keyOf(part));
          if (kept) return Promise.resolve(kept);
          return getHeatGridCameraOutcome(
            Config.middlewareApi,
            token,
            rtspBid,
            win,
            part,
            controller.signal,
          ).catch((error: unknown): HeatGridOutcome => {
            console.warn(
              '[observer-editor] heat history read threw for stream',
              rtspBid,
              (error as Error)?.message,
            );
            return {
              status: 'failed',
              code: 'network',
              reason: HEAT_GRID_READ_REASONS.unreachable,
            };
          });
        }),
      ).then(outcomes => {
        window.clearTimeout(timer);
        if (gridInFlightRef.current?.controller === controller) gridInFlightRef.current = null;
        if (requestId !== gridRequestIdRef.current) return; // superseded
        if (outcomes.some(o => o.status === 'cancelled') && !deadlinePassed) return; // ours, no longer wanted
        if (deadlinePassed) {
          // The service logs nothing for its own abort, so the deadline says so here.
          console.warn(
            '[observer-editor] heat history read timed out for stream',
            rtspBid,
            win,
            view,
            MIDDLEWARE_REQUEST_TIMEOUT_MS,
          );
        }
        // Each part's own answer is kept as it would be on its own read.
        outcomes.forEach((o, i) => {
          if (!deadlinePassed && o.status === 'ready' && gridAnswerDecodes(o.value, parts[i])) {
            gridCacheRef.current.set(keyOf(parts[i]), o);
          }
        });
        const failed = outcomes.find(o => o.status === 'failed');
        const settled: HeatGridOutcome = deadlinePassed
          ? { status: 'failed', code: 'timeout', reason: HEAT_GRID_READ_REASONS.timedOut }
          : (failed ??
            (outcomes.length === 1
              ? outcomes[0]
              : {
                  status: 'ready',
                  value: mergeHeatPartAnswers(
                    outcomes.map(o => (o as Extract<HeatGridOutcome, { status: 'ready' }>).value),
                  ),
                }));
        if (settled.status === 'ready' && gridAnswerDecodes(settled.value, view)) {
          gridCacheRef.current.set(key, settled);
          markHeatGrid('response');
        }
        setGrid({ key, state: settled });
      });
    },
    [],
  );

  // A feed becoming selected — on open, and on every feed change — reads its heat. It never changes
  // the Heatmap switch: that is the person's remembered choice, so heat turned off stays off on the
  // next camera (`onboarding-heatmap-remembered`, R4). The heat is still read while the switch is
  // off, so turning it on shows heat with no new request; toggling never reads (R5, and the
  // `heatVisible` state is deliberately not a dependency here): one request per feed selection,
  // plus Retry. The camera LEAVING the stage is not a selection: `leaveCamera` leaves the endpoint
  // in place, so without the guard the entity clearing fired a read for a camera no longer on
  // screen, and returning fired another. Leaving aborts a read in flight and disowns its answer
  // (`observers-page-camera-stage`, criterion 18).
  //
  // ⚠ NOT keyed on the entity (`camera-first-observers`, R12). The feed is what the heat is about;
  // in the camera workspace a pick changes the entity and never the feed, so a dependency on it
  // re-read the heat on every pick. In the entity editor the entity cannot change while the editor
  // is open, so the dependency did nothing there.
  // A tracked-part change is a new key into the same read: a part this camera and window already
  // answered comes from the cache, so picking between observers that track one part reads nothing.
  useEffect(() => {
    if (!editorOpen) {
      // Nobody is looking: a read in flight is aborted and its answer disowned, rather than left
      // to run to the deadline and write into a surface that has gone.
      gridRequestIdRef.current += 1;
      abortGridRead();
      return;
    }
    readGridHeat(selectedRtspBid, accessToken, heatWindow, heatView);
    // `abortGridRead` is a stable closure over refs; it is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen, selectedRtspBid, accessToken, readGridHeat, heatWindow, heatView]);

  /*
   * The engine's SUGGESTIONS (`observer-autogen-review-ui-preview`, R1-R3, R8): read-only, one
   * camera and one window per request, off until the admin turns it on. Ready answers are kept for
   * the life of the step by `{planId}|{rtspBid}|{window}`; failures are not. Turning the switch on
   * again, or Refresh, drops the current entry first. Nothing here touches the plan, the draft or
   * the observers - the read is the only thing it does.
   */
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  /*
   * The list panel (`onboarding-persistent-scene-observers-glass`, R2.6): open on every visit, never
   * stored; `[` folds and opens it from any view. Full screen is gone: the plan fills the page.
   */
  const [listOpen, setListOpen] = useState(true);
  /*
   * The list panel's header host (R2.2): the inventory renders its tabs, notice and sort headings
   * into it, under the search row. While review's machine list takes the body, nothing renders
   * into it and the header shows search alone (R2.4).
   */
  const [inventoryHeader, setInventoryHeader] = useState<HTMLDivElement | null>(null);
  /* Show map (R5.3): on at every visit, never stored. */
  const [showMap, setShowMap] = useState(true);
  // Suggestions read the heat window (`observer-autogen-review-layout`, L5.2 — the separate
  // suggestion window is gone).
  const suggestionWindow: HeatGridWindow = heatWindow;
  const [suggestionRead, setSuggestionRead] = useState<{
    key: string | null;
    state: SuggestionReadState;
  }>({ key: null, state: 'idle' });
  const suggestionCacheRef = useRef(new Map<string, ObserverProposalsOutcome>());
  const suggestionRequestIdRef = useRef(0);
  const suggestionInFlightRef = useRef<{ controller: AbortController; timer: number } | null>(null);
  const [suggestionNonce, setSuggestionNonce] = useState(0);
  const abortSuggestionRead = () => {
    const inFlight = suggestionInFlightRef.current;
    if (!inFlight) return;
    window.clearTimeout(inFlight.timer);
    inFlight.controller.abort();
    suggestionInFlightRef.current = null;
  };
  useEffect(() => () => abortSuggestionRead(), []);
  const suggestionKey =
    planId !== null && selectedRtspBid ? `${planId}|${selectedRtspBid}|${suggestionWindow}` : null;

  useEffect(() => {
    suggestionRequestIdRef.current += 1;
    abortSuggestionRead();
    if (!editorOpen || !suggestionsVisible || !suggestionKey || !accessToken || planId === null) {
      return;
    }
    const cached = suggestionCacheRef.current.get(suggestionKey);
    if (cached) {
      setSuggestionRead({ key: suggestionKey, state: cached });
      return;
    }
    const rtspBid = selectedRtspBid!;
    const requestId = suggestionRequestIdRef.current;
    // Bounded like the heat reads: the AbortSignal is the wall-clock deadline, axios's `timeout`
    // inside the read the guard on a stalled connect. The server answers inside its own 25 s.
    const controller = new AbortController();
    let deadlinePassed = false;
    const timer = window.setTimeout(() => {
      deadlinePassed = true;
      controller.abort();
    }, MIDDLEWARE_REQUEST_TIMEOUT_MS);
    suggestionInFlightRef.current = { controller, timer };
    setSuggestionRead({ key: suggestionKey, state: 'loading' });
    getObserverProposalsOutcome(
      Config.middlewareApi,
      accessToken,
      planId,
      rtspBid,
      suggestionWindow,
      controller.signal,
    )
      .catch(
        (): ObserverProposalsOutcome => ({
          status: 'failed',
          code: 'network',
          reason: OBSERVER_PROPOSALS_READ_REASONS.unreachable,
        }),
      )
      .then(outcome => {
        window.clearTimeout(timer);
        if (suggestionInFlightRef.current?.controller === controller) {
          suggestionInFlightRef.current = null;
        }
        if (requestId !== suggestionRequestIdRef.current) return; // superseded
        if (outcome.status === 'cancelled' && !deadlinePassed) return; // ours, no longer wanted
        if (deadlinePassed) {
          console.warn(
            '[observer-editor] suggestions read timed out for stream',
            rtspBid,
            suggestionWindow,
            MIDDLEWARE_REQUEST_TIMEOUT_MS,
          );
        } else if (outcome.status === 'failed') {
          console.warn(
            '[observer-editor] suggestions read failed for stream',
            rtspBid,
            suggestionWindow,
            outcome.code,
          );
        }
        const settled: ObserverProposalsOutcome = deadlinePassed
          ? { status: 'failed', code: 'timeout', reason: OBSERVER_PROPOSALS_READ_REASONS.timedOut }
          : outcome;
        if (settled.status === 'ready') suggestionCacheRef.current.set(suggestionKey, settled);
        setSuggestionRead({ key: suggestionKey, state: settled });
      });
    // `abortSuggestionRead` is a stable closure over refs; `suggestionNonce` forces a re-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen, suggestionsVisible, suggestionKey, accessToken, suggestionNonce]);

  const rereadSuggestions = useCallback(() => {
    if (suggestionKey) suggestionCacheRef.current.delete(suggestionKey);
    setSuggestionNonce(n => n + 1);
  }, [suggestionKey]);
  const changeSuggestionsVisible = useCallback(
    (on: boolean) => {
      // On again is a fresh look: drop this camera's kept answer (R2).
      if (on && suggestionKey) suggestionCacheRef.current.delete(suggestionKey);
      setSuggestionsVisible(on);
    },
    [suggestionKey],
  );
  const suggestionState: SuggestionReadState =
    suggestionsVisible && suggestionRead.key === suggestionKey ? suggestionRead.state : 'idle';
  const suggestions = selectedRtspBid
    ? suggestionView(suggestionState, selectedRtspBid, suggestionWindow)
    : null;

  // Stable identity while the person stays the same: the Heatmap switch's `onChange` in the layers card.
  const handleToggleHeatmap = useCallback(
    (enabled: boolean) => setHeatVisible(enabled),
    [setHeatVisible],
  );

  // The history's answer, read only while it is the selected camera's current combination — so a
  // switch never shows the previous combination's image under the new caption (Failure States).
  const gridKey = selectedRtspBid ? `${selectedRtspBid}|${heatWindow}|${heatView}` : null;
  const gridOutcome: GridHeatReadState = grid.key === gridKey ? grid.state : 'idle';
  const gridSettled = typeof gridOutcome === 'object' ? gridOutcome : null;
  const gridFailed = gridSettled?.status === 'failed' ? gridSettled : null;
  const retryGridHeat = useCallback(() => {
    // An answer that could not be decoded was cached as ready; Retry asks the server again rather
    // than re-showing it. (A failed read is never cached, so this only matters for that case.)
    if (selectedRtspBid) {
      for (const v of [heatView, ...HEAT_VIEW_PARTS[heatView]]) {
        gridCacheRef.current.delete(`${selectedRtspBid}|${heatWindow}|${v}`);
      }
    }
    readGridHeat(selectedRtspBid, accessToken, heatWindow, heatView);
  }, [readGridHeat, selectedRtspBid, accessToken, heatWindow, heatView]);
  // Decoded once per answer; a body that cannot be decoded is the decode row's Errored state.
  // Empty draws nothing: the caption says there is no heat, and no blank image claims otherwise.
  const gridCells = useMemo((): HeatGridCells | 'unreadable' | null => {
    if (gridSettled?.status !== 'ready') return null;
    try {
      return decodeHeatView(gridSettled.value, heatView);
    } catch (error) {
      console.warn(
        '[observer-editor] could not decode heat history for stream',
        selectedRtspBid,
        heatView,
        (error as Error).message,
      );
      return 'unreadable';
    }
  }, [gridSettled, heatView, selectedRtspBid]);
  // The picture's natural size, reported by the plotter; the image is sized from it (R4).
  // Held with the camera it was measured on, so a camera switch never paints at the last one's size.
  const [pictureSize, setPictureSize] = useState<{
    rtspBid: string | null;
    w: number;
    h: number;
  } | null>(null);
  const reportPictureSize = useCallback(
    (size: { w: number; h: number } | null) =>
      setPictureSize(size ? { rtspBid: selectedRtspBid, ...size } : null),
    [selectedRtspBid],
  );
  const heatGridImage = useHeatGridImage(
    gridCells && gridCells !== 'unreadable' ? gridCells : null,
    pictureSize && pictureSize.rtspBid === selectedRtspBid
      ? { w: pictureSize.w, h: pictureSize.h }
      : null,
  );
  // Decoded cells and a known picture size that still paint nothing mean the canvas refused: an
  // error with Retry, never a ready caption over a bare picture.
  const gridPaintFailed =
    !!gridCells &&
    gridCells !== 'unreadable' &&
    !!pictureSize &&
    pictureSize.rtspBid === selectedRtspBid &&
    !heatGridImage;
  const gridUnreadable = gridCells === 'unreadable' || gridPaintFailed;
  /*
   * The outlines round the heat on show (R3, R4): the same cells the heat image paints, at the
   * picture's size on this camera. None until the switch is on, the heat shows and both are known
   * (Inputs and Missing Values); a camera, window or view change recomputes them (Overtaken).
   */
  const heatOutlines = useMemo(() => {
    const size = pictureSize && pictureSize.rtspBid === selectedRtspBid ? pictureSize : null;
    if (!heatOutlinesOn || !heatVisible || !gridCells || gridCells === 'unreadable' || !size) {
      return NO_HEAT_OUTLINES;
    }
    return heatBlobOutlines(
      gridCells,
      { width: size.w, height: size.h },
      outlineEdge,
      HEAT_GRID_CELL_PX,
      { separatePeaks },
    );
  }, [
    heatOutlinesOn,
    heatVisible,
    gridCells,
    pictureSize,
    selectedRtspBid,
    outlineEdge,
    separatePeaks,
  ]);

  const heatmapLoading = gridOutcome === 'loading';
  const gridCaptionText = gridUnreadable ? '' : gridHeatCaption(gridOutcome, heatWindow, heatView);
  // A failed history read is said in the caption's polite region too, not only by the banner (A4).
  const gridFailureWords = gridFailed
    ? `${GRID_HEAT_CAPTIONS.failed} — ${gridFailed.reason}`
    : gridUnreadable
      ? `${GRID_HEAT_CAPTIONS.failed} — ${HEAT_GRID_READ_REASONS.unreadable}`
      : '';
  const heatCaptionText = gridCaptionText || gridFailureWords;
  /*
   * Said once per camera and window — never because only the tracked part changed
   * (`observers-heat-follows-tracked-part`, criterion 9). A part changes with every pick and every
   * body-part edit and its undo, and the stage has ONE polite region: saying the heat there would
   * overwrite the sentence the edit itself just said ("Undone: …"). The tracking card's pressed
   * part, the Heatmap switch's description and the heat image's name carry the part, and a failed
   * read is also said by its banner, which is an alert.
   *
   * What was last said: the camera and window, the part, and whether that was the first read still
   * pending or a failure — so the first read's answer, and Retry's after a failure, are said for the
   * same part. A part change clears both, which ends a pending first read unsaid (an edit and its
   * Undo put the part back; Copilot, PR #2210) and forgets a failure, so a part change after a failed
   * read is not said over the edit either (review, 2026-10-22).
   */
  const heatSaidForRef = useRef<{
    sight: string;
    part: HeatView;
    pending: boolean;
    failed: boolean;
  } | null>(null);
  useEffect(() => {
    const said = heatSaidForRef.current;
    if (said && said.part !== heatView) {
      heatSaidForRef.current = { ...said, part: heatView, pending: false, failed: false };
    }
  }, [heatView]);
  /*
   * Draw Observer's armed sentence (R3.6) is said as the camera opens, and the camera's first heat
   * sentence follows it through the same region before it is ever rendered: on the real app the
   * operator heard *Reading heat…* and never what to do. While this is set, the heat's sentences
   * carry the armed one after them, up to and including the read's answer, so whichever is left
   * standing says it.
   */
  const armSentencePendingRef = useRef(false);
  useEffect(() => {
    if (!editorOpen) {
      // A camera that comes back on stage is a fresh look: its heat is said again, even when the
      // caption is unchanged — which is why `editorOpen` is a dependency here.
      heatSaidForRef.current = null;
      armSentencePendingRef.current = false;
      return;
    }
    if (!heatCaptionText) return;
    const sight = `${selectedRtspBid}|${heatWindow}`;
    const said = heatSaidForRef.current;
    // A new camera or window; or, for the same part, the first read answering or Retry after a
    // failure. A part-only change matches neither and is not said.
    const speak =
      !said || said.sight !== sight || (said.part === heatView && (said.pending || said.failed));
    if (speak) {
      say(
        armSentencePendingRef.current
          ? `${heatCaptionText}${/[.…]$/.test(heatCaptionText) ? '' : '.'} ${ADD_OBSERVER_ARMED}`
          : heatCaptionText,
      );
      // *Reading heat…* is replaced by the read's answer: that answer carries it too.
      if (gridOutcome !== 'loading') armSentencePendingRef.current = false;
    }
    heatSaidForRef.current = {
      sight,
      part: heatView,
      // Only a read whose "Reading heat…" was said has its answer said.
      pending: speak && gridOutcome === 'loading',
      failed: !!(gridFailed || gridUnreadable),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatCaptionText, editorOpen]);
  const heatLayerSubline = gridUnreadable
    ? GRID_HEAT_CAPTIONS.failed
    : gridHeatSubline(gridOutcome, heatWindow, heatView);
  const gridShowsHeat = heatVisible && gridSettled?.status === 'ready' && !!heatGridImage;
  // Missing days are named whenever the answer is ready, heat drawn or not (never left to look quiet).
  const gridMissing =
    heatVisible && gridSettled?.status === 'ready' ? gridMissingNote(gridOutcome) : '';
  const changeHeatWindow = useCallback(
    (next: HeatGridWindow) => {
      setHeatWindow(next);
    },
    [setHeatWindow],
  );

  /**
   * Tell the store there is unsaved work.
   *
   * Set at once, in the edit's own handler, so the leave guard sees the edit in the same tick; the
   * write-through effect below puts the edit itself into the plan after the commit. The flag stays
   * set until the host's draft saver lands a save.
   */
  /** Bumped by every edit this stage makes. */
  const editRevisionRef = useRef(0);
  /*
   * The same count, as state, so an edit re-renders into the write-through effect below
   * (`observers-page-draft`, FR1).
   */
  const [editRevision, setEditRevision] = useState(0);
  const onObserverEditPendingRef = useRef(onObserverEditPending);
  onObserverEditPendingRef.current = onObserverEditPending;
  const markPlanDirty = useCallback(() => {
    editRevisionRef.current += 1;
    setEditRevision(r => r + 1);
    useFloorplanStore.getState().setDirty(true);
    onObserverEditPendingRef.current?.();
  }, []);

  /*
   * ── One undo history for the page (`observers-page-camera-stage`, FR16–FR17, ruling R3) ────────
   *
   * Every edit records the session as it stood BEFORE it; Ctrl+Z and a toast's *Undo* both pop the
   * newest. A toast belongs to the entry it announces, and is dismissed the moment another entry is
   * recorded — so a toast's Undo is always the newest step, never an older one reached past newer
   * work. Saves never clear it (a draft save is not an edit); a load does (`clearHistory`), and the
   * step leaving does. Each undo is itself an edit, so the draft saves it.
   */
  type HistoryEntry = {
    snapshot: ObserverSessionSnapshot;
    change: ObserverChange;
    /** Where the stage goes back to, for an edit made from a stage view (Remove Camera). */
    view?: { stage: StageView; pick: SelectedEntity };
    toastId?: string;
  };
  const historyRef = useRef(createUndoStack<HistoryEntry>(50));
  /** The session as it stands NOW — a toast's Undo runs long after the render that offered it. */
  const sessionForUndoRef = useRef({ polygonsByEntity, observerState });
  sessionForUndoRef.current = { polygonsByEntity, observerState };
  /** A corner drag in progress, so it records ONE step however many moves it reports (FR16). */
  const editGestureRef = useRef({ active: false, recorded: false });
  /*
   * The step's sentences — an undo, a link, a refresh's answer, the heat read's outcome — said
   * through the stage's ONE polite region (Block A4, FR26), the newest winning. The stage says its
   * own title on a change of view; a sentence given in the same commit is said after it.
   */
  const [stageNote, setStageNote] = useState('');
  const say = (text: string) => setStageNote(prev => nextAnnouncement(prev, text));

  /*
   * Folding the list panel, from any of its controls or `[` (R2.6, R2.7, Block A4). A fold made
   * while focus is inside the panel (`[` from a row or tab) sends focus to the folded bar's »:
   * `ListPanel` does that for any host fold (Block A1).
   */
  const onListOpenChange = (open: boolean) => {
    setListOpen(open);
    say(open ? 'Inventory list opened' : 'Inventory list folded');
  };
  const toggleList = () => onListOpenChange(!listOpen);
  const toggleListRef = useRef(toggleList);
  toggleListRef.current = toggleList;

  /*
   * *Add Observer* arms one region for the picked entity on the open camera
   * (`observer-unified-draw-edit`, R4.4): a new region, not a redraw of the selected one. One
   * function for the item card's button and the likely cameras' Draw Observer (R3.6).
   */
  const armAddObserver = () => {
    setAddObserverArmed(true);
    setPendingEndpointId(selectedEndpointId);
    setSelectedObserverId(null);
    say(ADD_OBSERVER_ARMED);
  };
  const armAddObserverRef = useRef(armAddObserver);
  armAddObserverRef.current = armAddObserver;
  /*
   * Draw Observer opens the camera, THEN arms: arriving on a camera disarms (the effect above, on
   * `cameraEntry` and `editorOpen`), so the arm waits for the camera to be open on the endpoint it
   * was asked for. Declared after the disarm effect, so it runs after it in the same commit.
   */
  const armOnEntryRef = useRef<number | null>(null);
  useEffect(() => {
    const want = armOnEntryRef.current;
    if (want == null || !editorOpen || selectedEndpointId !== want) return;
    armOnEntryRef.current = null;
    armSentencePendingRef.current = true;
    armAddObserverRef.current();
  }, [editorOpen, selectedEndpointId, cameraEntry]);
  // Disarmed before the heat spoke (Esc, a first point): the heat says its own sentence alone.
  useEffect(() => {
    if (!addObserverArmed) armSentencePendingRef.current = false;
  }, [addObserverArmed]);
  /*
   * How many heat outlines show (R7): said when they appear, for a new camera or window, when the
   * edge is released, and when Separate peaks changes (`observer-matching-beyond-geometry-region-split`,
   * R4, which bumps the same counter); never on every step of a drag, and never for a body-part change alone,
   * which would overwrite Undo's own sentence (as the heat caption's rule). Not worked out yet
   * (no heat or no picture size) says nothing; worked out and empty says there is no heat above
   * the edge.
   */
  const heatOutlinesWorkedOut = heatOutlines !== NO_HEAT_OUTLINES;
  useEffect(() => {
    if (heatOutlinesOn && heatOutlinesWorkedOut) {
      say(heatOutlineCountText(heatOutlines.length, outlineEdge));
    }
    // The count and the edge are read, not watched: a drag step or a new count alone says nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatOutlinesOn, outlineEdgeCommits, heatOutlinesWorkedOut, selectedRtspBid, heatWindow]);
  const recordUndo = (change: ObserverChange, view?: HistoryEntry['view']) => {
    const top = historyRef.current.peek();
    if (top?.toastId) toastRef.current.dismiss(top.toastId);
    const { polygonsByEntity: p, observerState: o } = sessionForUndoRef.current;
    historyRef.current.push({ snapshot: { polygonsByEntity: p, observerState: o }, change, view });
  };
  /** Hand the newest entry the toast that announces it. */
  const attachToast = (id: string) => {
    const top = historyRef.current.peek();
    if (top) top.toastId = id;
  };
  /** Only the newest entry can hold a live toast: recording anything newer dismissed the rest. */
  const clearHistory = () => {
    const id = historyRef.current.peek()?.toastId;
    if (id) toastRef.current.dismiss(id);
    historyRef.current.clear();
  };
  /** What the camera is showing NOW, for an undo run from an older closure (a toast's). */
  const undoNowRef = useRef<{
    endpointId: number | null;
    selectedObserverId: string | null;
    waiting: boolean;
  }>({ endpointId: null, selectedObserverId: null, waiting: false });
  const undo = () => {
    if (!stageMountedRef.current) return;
    const entry = historyRef.current.pop();
    if (!entry) {
      say('Nothing to undo');
      return;
    }
    if (entry.toastId) toastRef.current.dismiss(entry.toastId);
    const { polygonsByEntity: p, observerState: o } = entry.snapshot;
    const now = undoNowRef.current;
    setPolygonsByEntity(p);
    setObserverState(o);
    /*
     * The selection must name an observer that is on the camera on stage again. One the restored
     * session puts on another camera, or on none (an undone link), is let go: the camera is held.
     */
    const prev = now.selectedObserverId;
    if (prev && o[prev]?.endpointId !== now.endpointId) {
      setSelectedObserverId(null);
      setPendingEndpointId(now.endpointId);
    }
    // The drawing area reseeds from the restored session (it holds its own copy of the regions),
    // and a drag or nudge it was in the middle of ends with it.
    setCameraEntry(n => n + 1);
    editGestureRef.current = { active: false, recorded: false };
    // The toast whose Undo held focus is dismissed: focus goes to the picture, if nowhere else.
    if (editorOpenRef.current) cameraFocusPendingRef.current = true;
    setRegionClosedHere(false);
    setWaitingRegion(null);
    markPlanDirty();
    if (entry.view && !editorOpenRef.current) {
      setStagePick(entry.view.pick);
      setStage(entry.view.stage);
    }
    say(
      `Undone: ${changeLine(entry.change)}${
        now.waiting ? ". The region that was waiting wasn't linked, so it was discarded." : ''
      }`,
    );
  };
  /** A toast's Undo: only its own entry, which recording anything newer has already dismissed. */
  const undoFromToast = (id: string) => {
    if (historyRef.current.peek()?.toastId === id) undo();
  };
  const editorOpenRef = useRef(false);

  /*
   * Write-through: every edit reaches the plan store as it is made (`observers-page-draft`, FR1),
   * so leaving the step, reloading or being saved by the host's draft saver loses nothing.
   *
   * ⚠ **An effect on the counter, never a call beside `markPlanDirty`.** Every edit path calls it in
   * the same handler as its `setState`, so a write made there would build the plan from the state as
   * it was BEFORE the edit. The effect runs after the commit, on the state that includes it.
   *
   * Hydration, the camera workspace's waiting region, a pending *Add another camera* and *Observer
   * still correct* do not advance the counter, so none of them write — they are not edits.
   *
   * **At most once per animation frame.** Dragging a vertex reports an edit on every mouse move,
   * and each write rebuilds every floor's observers and replaces the whole plan document, which
   * every page-level reader then re-renders on. So a burst of edits writes once, on the next frame,
   * from the newest committed state. **Leaving the step never drops one:** the unmount below writes
   * a frame still waiting, at once.
   */
  const writeThroughRef = useRef<() => void>(() => undefined);
  writeThroughRef.current = () => {
    writeObserversIntoPlan();
    onObserverEdit?.();
  };
  useEffect(() => {
    if (!editRevision || writeFrameRef.current != null) return;
    writeFrameRef.current = requestAnimationFrame(() => {
      writeFrameRef.current = null;
      writeThroughRef.current();
    });
  }, [editRevision]);
  /**
   * Write a frame still waiting, at once — on leaving the step, and before a draft swap, which can now
   * start with this step on screen (`twin-details-page-versions`, R8.3). Nothing waiting: nothing to do.
   */
  const flushPendingWrite = useCallback(() => {
    if (writeFrameRef.current == null) return;
    cancelAnimationFrame(writeFrameRef.current);
    writeFrameRef.current = null;
    writeThroughRef.current();
  }, []);
  useEffect(
    () => registerPendingWriteFlush?.(flushPendingWrite),
    [registerPendingWriteFlush, flushPendingWrite],
  );
  useEffect(() => () => flushPendingWrite(), [flushPendingWrite]);

  /*
   * There is no per-observer frame fetch any more, and its absence is the point.
   *
   * Feature 04 had to add one: the picker only rendered a tile per PLAN camera, so on a twin whose
   * observers name streams nobody has placed — `NP2-STN` carries 151 observers and zero placed
   * cameras — nothing would ever ask for the open observer's picture. The wall now loads every
   * feed on the twin, so the special case has nothing left to be special about, and the capture it
   * used to publish per observer is replaced by a stored-frame read shared with the picker.
   */

  /**
   * The open entity's observers, in draw order.
   *
   * Only completed polygons: a half-drawn region is not yet an observer, and putting it in the
   * chooser would let an operator select something that does not exist.
   */
  const selectedObservers = useMemo(() => {
    if (!selected) return [];
    return (polygonsByEntity[selected.refid] ?? [])
      .filter(p => p.isComplete)
      .map(p => observerRefid(selected.refid, p.id));
  }, [selected, polygonsByEntity]);

  /** The open entity's observers grouped by camera — the cameras it is on, and whether it has any. */
  const cameraGroups = useMemo(
    () =>
      selected
        ? observerCameraGroups(
            selected.refid,
            polygonsByEntity[selected.refid] ?? [],
            observerState,
          )
        : [],
    [selected, polygonsByEntity, observerState],
  );
  /** Complete observers on the open entity — `observerTip`'s sibling count, which decides "· new". */
  const entityObserverCount = selectedObservers.length;

  /*
   * The open entity's observers on the camera SHOWN, in filed order, each with its filed name — what
   * the observer strip lists. Moving between cameras is the stage's side by side; the strip only chooses
   * among the regions on the picture in front of the operator (a 360° camera carrying several).
   */
  const cameraObservers = useMemo(() => {
    if (!selected) return [];
    const group = cameraGroups.find(g => g.endpointId === selectedEndpointId);
    return byFiledOrder(
      (group?.observers ?? []).map(o => ({
        id: o.refid,
        tip: tipForObserver(selected.refid, o.polygon.id, entityObserverCount),
      })),
    );
  }, [selected, cameraGroups, selectedEndpointId, entityObserverCount, tipForObserver]);

  /*
   * What the editor's polite status region says: which camera is being drawn on. Written through
   * state AFTER the region mounts, never with it: a live region inserted with its text already in it
   * is not reliably announced. (Entities on several cameras are side by side on the stage now, which
   * announces itself — `observers-page-stage`.)
   */
  const statusWanted =
    workspace && waitingRegion
      ? "Region drawn. Pick what it watches from the list. It isn't saved until you do."
      : observersStatus === 'pending' && selectedEndpointId != null
        ? // The backdrop shows only this session's regions until the read lands (R19).
          'Checking which regions are already on this camera\u2026'
        : selectedFeed
          ? `Drawing on ${selectedFeed.name}`
          : '';
  const [editorStatus, setEditorStatus] = useState('');
  // Only the camera's name is not said again: the stage's title already says it.
  const editorStatusQuiet = !!selectedFeed && editorStatus === `Drawing on ${selectedFeed.name}`;
  useEffect(() => {
    if (editorStatus && !editorStatusQuiet) say(editorStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorStatus]);
  /*
   * A one-shot sentence that must survive the view change it caused. *Link* says what it did and then
   * moves the pick — which changes `statusWanted`, and the
   * effect below would write straight over the sentence on the next commit. So the note rides along
   * in front of it, once. The effect after it drops a note nobody consumed — a removal that left the
   * line unchanged, where the handler's own write already said it — keyed on WHERE the editor is
   * rather than on every commit: a commit already pending when the button was pressed flushes its
   * effects first, and an every-commit clear dropped the note there, before the move it described
   * (observed while building, on the three-camera removal).
   */
  const statusNoteRef = useRef<string | null>(null);
  useEffect(() => {
    const note = statusNoteRef.current;
    statusNoteRef.current = null;
    setEditorStatus(editorOpen ? (note ? `${note} ${statusWanted}`.trim() : statusWanted) : '');
  }, [editorOpen, statusWanted]);
  useEffect(() => {
    statusNoteRef.current = null;
  }, [selected?.refid, selectedEndpointId, cameraEntry]);

  // On the WALL's feed for that camera: with none (a stream the twin's list does not carry, or the
  // list being re-read) the wall's refresh has nothing to act on and would never answer.
  const canRefreshPicture = selectedFeed != null;

  /*
   * Refresh picture: the wall's own per-feed refresh, pointed at the camera being drawn on. Whether
   * it is busy is the WALL's `connecting` state, never a local flag that could drift from it; what it
   * says is written into the editor's polite status region.
   *
   * The request is remembered by camera, and forgotten when the camera shown changes, so a late
   * answer for a camera no longer on screen says nothing. It concludes only after the wall has been
   * seen `connecting` for it, so a feed already failing does not report its OLD failure the instant
   * the button is pressed.
   *
   * An outcome is a statement about the picture AS IT THEN WAS, so it is taken back — the line goes
   * back to naming the camera — as soon as the picture moves on without this button (a thumbnail's
   * own refresh, a later failure). Left standing, "Couldn't get a fresh picture" would sit beside a
   * picture that has since arrived.
   */
  const feedState = selectedFeed?.state;
  const feedLabel = selectedFeed?.statusLabel;
  const feedImage = selectedFeed?.imageUrl;
  const feedCapturedAt = selectedFeed?.capturedAt?.getTime() ?? null;
  const refreshRequestRef = useRef<{
    endpointId: number;
    imageUrl: string | undefined;
    capturedAt: number | null;
    sawConnecting: boolean;
  } | null>(null);
  const refreshOutcomeRef = useRef<{
    endpointId: number;
    state: string | undefined;
    imageUrl: string | undefined;
  } | null>(null);
  const refreshPicture = () => {
    if (selectedEndpointId == null || !selectedFeed) return;
    refreshOutcomeRef.current = null;
    refreshRequestRef.current = {
      endpointId: selectedEndpointId,
      imageUrl: feedImage,
      capturedAt: feedCapturedAt,
      sawConnecting: false,
    };
    setEditorStatus('Asking the camera for a fresh picture\u2026');
    refreshFeed(selectedEndpointId);
  };
  useEffect(() => {
    const outcome = refreshOutcomeRef.current;
    if (outcome) {
      if (
        outcome.endpointId !== selectedEndpointId ||
        outcome.state !== feedState ||
        outcome.imageUrl !== feedImage
      ) {
        refreshOutcomeRef.current = null;
        // On another camera or view the line is already that one's; here it goes back to naming this.
        if (outcome.endpointId === selectedEndpointId) {
          setEditorStatus(statusWanted);
        }
      }
    }
    const request = refreshRequestRef.current;
    if (!request) return;
    if (!editorOpen || request.endpointId !== selectedEndpointId) {
      refreshRequestRef.current = null;
      return;
    }
    if (feedState === 'connecting') {
      request.sawConnecting = true;
      return;
    }
    if (!request.sawConnecting) return;
    refreshRequestRef.current = null;
    let sentence: string | null = null;
    if (feedState === 'healthy' || feedState === 'warning') {
      // "Updated" only for a NEWER picture. The wall mints a fresh object URL for every fetch, so the
      // capture instant is the test; a box that answers from its own cache sends the old moment back.
      const newer =
        feedImage !== request.imageUrl &&
        (request.capturedAt == null ||
          feedCapturedAt == null ||
          feedCapturedAt > request.capturedAt);
      sentence = newer ? 'Picture updated' : 'The camera sent back the picture it already had.';
    } else if (feedState === 'not_configured') {
      sentence = "This camera isn't set up on an edge box, so it can't send a fresh picture.";
    } else if (feedState === 'error') {
      // The wall's own reason, or none — never one invented here. "Still shown" only when there IS a
      // picture: a feed that never had one would make it untrue.
      sentence =
        `Couldn't get a fresh picture${feedLabel ? ` (${feedLabel})` : ''}.` +
        (feedImage ? ' The last picture is still shown.' : '');
    }
    if (sentence) {
      refreshOutcomeRef.current = {
        endpointId: request.endpointId,
        state: feedState,
        imageUrl: feedImage,
      };
      setEditorStatus(sentence);
    }
    // `statusWanted` is read only to restore the line, never a reason to re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen, selectedEndpointId, feedState, feedLabel, feedImage, feedCapturedAt]);

  /** A camera's name as the stage already names it: the stream's description, then its BID. */
  const cameraNameOf = (endpointId: number): string => {
    const stream = streams.find(e => e.id === endpointId);
    return stream?.description || stream?.bid || `Camera ${endpointId}`;
  };

  const labelFor = (refid: string, kind: ObserverTargetKind): string => {
    if (kind === 'floor') return entityLabels.get(refid)?.label ?? 'Floor';
    // The twin-wide name first: a region on the picture can be another floor's entity.
    const named = entityLabels.get(refid)?.label;
    if (named) return named;
    if (kind === 'surface') {
      const s = surfaces.find(s => s.refid === refid);
      return (s && surfaceName(s)) || 'Untitled zone';
    }
    const i = items.find(i => i.refid === refid);
    return i?.name || 'Equipment';
  };

  /**
   * One camera on the stage, for one entity (`observers-page-camera-stage`, FR1). Every way in
   * arrives here: a pick of an entity on one camera, a side-by-side tile, a suggested camera, a camera
   * being added, a chip on the entity card, and a pick that keeps the camera on stage (FR14).
   * `endpointId: null` is the entity's regions linked to no camera.
   *
   * The same rule `editCamera` uses: on a camera the entity is already on, its first observer there
   * is selected; on any other camera the camera is held as PENDING, so the next region drawn is the
   * entity's first on it.
   *
   * `from` is kept when the camera is already on stage (a pick that sticks, a chip): Esc still goes
   * back to where the camera was first opened from.
   */
  const openEditor = (
    refid: string,
    kind: ObserverTargetKind,
    endpointId: number | null,
    { from }: { from?: StageView } = {},
  ) => {
    if (from || !editorOpen) {
      // Focus goes back to whatever opened the camera — a row, a tile — when it leaves (FR23).
      rememberOpener();
      setCameraFrom(from ?? stage);
    }
    const own = polygonsByEntity[refid] ?? [];
    enterOnCamera(observerCameraGroups(refid, own, observerState), endpointId);
    setWorkspace(null);
    setWaitingRegion(null);
    setSelected({ refid, kind, label: labelFor(refid, kind) });
    setStagePick({ refid, kind, label: labelFor(refid, kind) });
  };

  /**
   * The one rule for arriving on a camera, shared by `openEditor` and `editCamera`: its first
   * observer there selected, or the camera held as PENDING when the entity is not on it yet. The
   * picker opens only for want of a picture, and remembers WHY, so the picture arriving a moment
   * later can close it again (below). Returns the entity's group on that camera, if any.
   */
  const enterOnCamera = (groups: readonly ObserverCameraGroup[], endpointId: number | null) => {
    const group = groups.find(g => g.endpointId === endpointId);
    setSelectedObserverId(group?.observers[0]?.refid ?? null);
    setPendingEndpointId(group ? null : endpointId);
    enterCameraView();
    setRegionClosedHere(false);
    return group;
  };

  /**
   * An entity picked from the list or the plan — the same result either way (FR18). None of its
   * regions on a camera: the cameras likely to see it. One camera: straight to that camera, in the
   * editor. Two or more: side by side on the stage.
   */
  const pickEntity = (
    refid: string,
    kind: ObserverTargetKind,
    {
      fromPlan = false,
      floorIndex,
    }: {
      fromPlan?: boolean;
      /**
       * The entity's floor, from the title-bar search, which picks across floors. Switched to only
       * once the camera on stage has let go — a camera that keeps the pick keeps its floor, because
       * the floor follows the camera (`openCameraWorkspace`).
       */
      floorIndex?: number | null;
    } = {},
  ) => {
    /*
     * A camera on stage: does it stay (FR14)? It stays when the next entity has a region on it, is
     * in its cone on the plan, or — camera by camera — was picked from this camera's own lists in
     * the navigator, every row of which is this camera's. Staying, the pick picks (or links the
     * waiting region) on this camera; otherwise the stage goes where picking it from the plan would.
     */
    if (editorOpen && selectedEndpointId != null) {
      const sticks = cameraSticks({
        refid,
        endpointId: selectedEndpointId,
        polygonsByEntity,
        observerState,
        inView: feedSight(sessionLayout, selectedEndpointId)?.inView ?? null,
        fromCameraList: !!workspace && !fromPlan,
      });
      if (sticks) {
        if (workspace) pickInWorkspace(refid, kind);
        else if (selected?.refid !== refid) openEditor(refid, kind, selectedEndpointId);
        return;
      }
    }
    if (floorIndex != null && floorIndex !== activeFloorIndex) {
      useFloorplanStore.getState().setActiveFloor(floorIndex);
    }
    const groups = observerCameraGroups(refid, polygonsByEntity[refid] ?? [], observerState);
    const where = stageViewForPick(groups.length);
    // Off the camera without handing focus back to what opened it: it stays on what was pressed.
    if (editorOpen) leaveCamera(PLAN_VIEW, { returnFocus: false });
    setStagePick({ refid, kind, label: labelFor(refid, kind) });
    // Regions on no camera only: there is no picture to open. The cameras to link them to (FR21).
    if (where === 'camera' && groups[0].endpointId == null) {
      setStage({ view: 'suggestions', refid, kind, linking: true });
      return;
    }
    if (where === 'camera') {
      // Straight to its one camera, opened from the plan (Esc goes there).
      setStage(PLAN_VIEW);
      openEditor(refid, kind, groups[0].endpointId, { from: PLAN_VIEW });
      return;
    }
    // The likely ones; with nothing placed to judge by, the stage shows every camera (`shownStage`).
    setStage({ view: where, refid, kind });
  };

  /** A pick on the plan in the corner: never one of the camera's own lists (FR13, FR14). */
  const pickFromCorner = (refid: string, kind: ObserverTargetKind) =>
    pickEntity(refid, kind, { fromPlan: true });

  /*
   * Onto another of the open entity's cameras, inside the editor (R4, R5). The camera's first
   * observer is selected, so the seed, the Track control and the strip all act on this camera. The
   * no-camera group opens with the picker, because there is no picture to draw on until one is chosen.
   */
  const editCamera = (endpointId: number | null) => {
    if (!selected) return;
    enterOnCamera(cameraGroups, endpointId);
  };

  /*
   * Onto the camera: the drawing area seeded afresh (`cameraEntry`), and focus handed to its canvas,
   * because the tile, row or chip that held it has usually just left the stage.
   */
  const cameraFocusPendingRef = useRef(false);
  const enterCameraView = () => {
    setCameraEntry(n => n + 1);
    editGestureRef.current = { active: false, recorded: false };
    cameraFocusPendingRef.current = true;
  };
  /** The camera view's drawing area, where the focus hand-off looks for the canvas. */
  const editorBodyRef = useRef<HTMLDivElement | null>(null);

  /*
   * *Side by Side* on the entity card (FR8): off the camera to the entity's cameras side by side.
   * Replaces the pop-up's *Back to all cameras*.
   */
  const sideBySideFromCamera = () => {
    if (!selected) return;
    const { refid, kind, label } = selected;
    leaveCamera({ view: 'side-by-side', refid, kind });
    setStagePick({ refid, kind, label });
  };
  /** *Add Camera* on the entity card (FR8): the likely cameras for it, a pick adding one. */
  const addCameraFromCamera = () => {
    if (!selected) return;
    const { refid, kind, label } = selected;
    leaveCamera({ view: 'suggestions', refid, kind, adding: true });
    setStagePick({ refid, kind, label });
  };
  /*
   * Every commit, not on `[cameraEntry]`, and the request is kept until a canvas exists
   * to take it: a focus asked for on a commit that cannot deliver it must not be spent there
   * (camera-first-observers, found by the rig's keyboard walk). The checks are two ref reads.
   */
  useEffect(() => {
    const body = editorBodyRef.current;
    if (cameraFocusPendingRef.current) {
      const canvas = body?.querySelector<HTMLElement>(
        `[${OBSERVER_PLOTTER_ATTRIBUTE}][role="application"]`,
      );
      if (!canvas) return;
      cameraFocusPendingRef.current = false;
      /*
       * A frame later, not now. A view that has just mounted may move focus in its own effects,
       * and under React's development StrictMode its effects run, clean up (returning focus to the
       * trigger) and run again AFTER this one — so a focus given here was taken straight back.
       * Delivered after every effect of the commit has run, it lands where it was asked for — but
       * only if nobody has placed focus since: on the page or the dialog's own surface. A row, a
       * button or anything else the operator moved to in that frame keeps it.
       */
      requestAnimationFrame(() => {
        const active = document.activeElement;
        // Unplaced: on the page, on a control the view change has just taken away, or parked on the
        // stage by the likely cameras panel as it closed (STAGE_ROOT_ATTRIBUTE).
        const unplaced =
          !active ||
          active === document.body ||
          !active.isConnected ||
          active.hasAttribute(STAGE_ROOT_ATTRIBUTE);
        if (canvas.isConnected && unplaced) canvas.focus();
      });
    }
  });

  /** What opened the camera, by what it names: a camera's tile, or an entity's row. */
  const openerPlaceRef = useRef<{ endpoint?: string; refid?: string } | null>(null);
  const rememberOpener = () => {
    const opener = document.activeElement;
    const el = opener instanceof HTMLElement && opener !== document.body ? opener : null;
    editorOpenerRef.current = el;
    const tile = el?.closest<HTMLElement>('li[data-endpoint]');
    const row = el?.closest<HTMLElement>('[data-refid]');
    openerPlaceRef.current = tile
      ? { endpoint: tile.dataset.endpoint }
      : row
        ? { refid: row.getAttribute('data-refid') ?? undefined }
        : null;
  };
  /*
   * FR1: an entity no longer in the layout (a reload, a re-match) cannot stay on stage. Its camera
   * goes back to where it was opened from, rather than keep writing regions no floor will file.
   */
  const pickGone = !!selected && floorEntityIndex.size > 0 && !floorEntityIndex.has(selected.refid);
  useEffect(() => {
    if (pickGone) leaveCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickGone]);

  /** Where focus goes once the camera has left the stage: what opened it, if it is still there. */
  const cameraLeftFocusRef = useRef(false);
  /**
   * The camera off the stage (FR23): to `to`, or back to where it was opened from. A region waiting
   * for its entity is left behind, and said so; a heat read in flight is aborted by the heat effect.
   */
  const leaveCamera = (to?: StageView, { returnFocus = true }: { returnFocus?: boolean } = {}) => {
    if (waitingRegion) {
      // The camera's own status line leaves with the camera: the step's region says it.
      say("The region that was waiting wasn't linked, so it was discarded.");
    }
    setSelected(null);
    setSelectedObserverId(null);
    setWorkspace(null);
    setWaitingRegion(null);
    setShortcutsOpen(false);
    setDrawingPoints(0);
    editGestureRef.current = { active: false, recorded: false };
    pictureHoverStore.set(null);
    setStage(to ?? cameraFrom);
    cameraLeftFocusRef.current = returnFocus;
    if (!returnFocus) {
      editorOpenerRef.current = null;
      openerPlaceRef.current = null;
    }
  };
  useEffect(() => {
    if (!cameraLeftFocusRef.current || editorOpen) return;
    cameraLeftFocusRef.current = false;
    const opener = editorOpenerRef.current;
    const where = openerPlaceRef.current;
    editorOpenerRef.current = null;
    openerPlaceRef.current = null;
    if (opener?.isConnected) {
      opener.focus();
      return;
    }
    /*
     * The tile or row that opened the camera was unmounted while the camera was on stage, so its
     * successor is found by what it named: the same camera's tile, or the same entity's row (FR23).
     */
    const body = stageBodyRef.current;
    if (!body || !where) return;
    const match =
      where.endpoint !== undefined
        ? Array.from(body.querySelectorAll<HTMLElement>('.stage li[data-endpoint]')).find(
            li => li.dataset.endpoint === where.endpoint,
          )
        : Array.from(body.querySelectorAll<HTMLElement>('[data-refid]')).find(
            el => el.getAttribute('data-refid') === where.refid,
          );
    match?.querySelector<HTMLElement>('button, [role="button"]')?.focus();
  });

  /*
   * ── The camera workspace's handlers (`camera-first-observers`) ────────────────────────────────
   *
   * Plain functions, not `useCallback`, for the reason `openEditor` is one: they read this render's
   * `polygonsByEntity`, `observerState` and floor arrays, and a frozen closure over them is the
   * defect that once titled a cross-floor entity "Equipment".
   */

  /** Where focus goes once the view the operator acted in has been replaced. */
  const workspaceFocusRef = useRef<{ kind: 'row'; refid: string } | null>(null);

  /**
   * Open the workspace fixed to one camera (R3) — from *Choose a camera* or the Cameras view.
   *
   * The floor follows the camera: a camera placed on another floor switches the step to it, so the
   * panel, the floor row and the attention line all describe the floor the camera can see. A feed
   * with no camera on the plan leaves the floor as it is.
   */
  const openCameraWorkspace = (endpointId: number) => {
    const sight = feedSight(sessionLayout, endpointId);
    if (sight && sight.floorIndex !== activeFloorIndex) {
      useFloorplanStore.getState().setActiveFloor(sight.floorIndex);
    }
    if (!editorOpen) {
      rememberOpener();
      setCameraFrom(stage);
    }
    if (waitingRegion) {
      statusNoteRef.current = "The region that was waiting wasn't linked, so it was discarded.";
    }
    setSelected(null);
    setStagePick(null);
    setSelectedObserverId(null);
    setPendingEndpointId(null);
    setWaitingRegion(null);
    setRegionClosedHere(false);
    setWorkspace({ endpointId });
    enterCameraView();
  };

  /**
   * A row pressed in the workspace's panel (R6, R7). The camera never changes.
   *
   * With a region waiting, the press LINKS it: the region becomes a new observer of that entity on
   * this camera, stamped like any drawn region, and nothing stays picked (Rob, 2026-09-28) — so the
   * next region drawn waits again. Without one, the press picks the entity: its regions on this
   * camera become editable and its first observer here is selected.
   */
  const pickInWorkspace = (refid: string, kind: ObserverTargetKind) => {
    if (!workspace || workspace.endpointId == null) return;
    if (selected?.refid === refid) return;
    const endpointId = workspace.endpointId;
    const label = labelFor(refid, kind);
    // The pick. Every press makes one; a link below then decides whether it stays.
    setSelected({ refid, kind, label });
    // Reseed the plotter from the new state: the entity's regions on this camera, or — after a
    // link, with nothing picked — empty, so the linked region is not left in the plotter as an
    // editable polygon on top of its new backdrop copy.
    setCameraEntry(n => n + 1);
    if (waitingRegion) {
      recordUndo({ kind: 'linked', entity: label });
      const linked = linkRegion(
        polygonsByEntity,
        observerState,
        refid,
        waitingRegion,
        endpointId,
        newRegionTrackSide,
      );
      setPolygonsByEntity(linked.polygonsByEntity);
      setObserverState(linked.observerState);
      stampObservers([linked.observerRefid], geometryOf(sessionLayout, refid));
      markPlanDirty();
      setWaitingRegion(null);
      // R7.4 (Rob, 2026-09-28): after a link NOTHING stays picked, so the next region drawn waits
      // for its own pick. Keeping the linked entity picked instead is removing these two lines.
      setSelected(null);
      setStagePick(null);
      setSelectedObserverId(null);
      statusNoteRef.current = `Linked to ${label}.`;
      workspaceFocusRef.current = { kind: 'row', refid };
      return;
    }
    // Its first observer ON THIS CAMERA, so the strip, the Track control and *Observer still
    // correct* act on the picture in front of the operator. None: the next region drawn is its
    // first here (R6).
    const onThisCamera = observerCameraGroups(
      refid,
      polygonsByEntity[refid] ?? [],
      observerState,
    ).find(g => g.endpointId === endpointId)?.observers[0]?.refid;
    setSelectedObserverId(onThisCamera ?? null);
    setStagePick({ refid, kind, label });
  };

  /** Clear the pick (R9). A half-drawn region goes with it; the next region drawn waits. */
  const deselect = () => {
    if (!workspace || !selected) return;
    setSelected(null);
    setStagePick(null);
    setSelectedObserverId(null);
    // Nothing waits while something is picked (R7.2); the reseed below drops any half-drawn region.
    enterCameraView();
  };

  // Focus after a link: the linked row, which has usually just moved group. Runs after the commit
  // that re-rendered the panel.
  useEffect(() => {
    const request = workspaceFocusRef.current;
    if (!request) return;
    const body = stageBodyRef.current;
    if (!body) return;
    workspaceFocusRef.current = null;
    // Matched by attribute value rather than a selector string: a refid is data, not CSS.
    const row = Array.from(body.querySelectorAll<HTMLElement>('[data-refid]'))
      .find(el => el.getAttribute('data-refid') === request.refid)
      ?.querySelector<HTMLElement>('button.row-select');
    (row ?? body.querySelector<HTMLElement>('.panel-scroll'))?.focus();
  });

  const handlePolygonChange = (polygons: Polygon[]) => {
    /*
     * The camera workspace with nothing picked: the plotter runs single-polygon, and what it holds
     * is the WAITING region (R7) — mirrored out, never a plan edit, never a dirty mark.
     *
     * ⚠ Before the seed check, not after it. *Clear Observer* on a waiting region reports `[]`
     * against an empty seed, which the seed check below reads as the plotter seeding itself and
     * ignores — leaving a region in the slot that is no longer on the picture, for the next pick to
     * link (R10a).
     */
    if (workspace && !selected) {
      const complete = polygons.filter(p => p.isComplete);
      setWaitingRegion(complete[complete.length - 1] ?? null);
      return;
    }
    if (!selected) return;
    const targetId = selected.refid;

    /*
     * ⚠ Is this an EDIT, or the plotter seeding itself?
     *
     * `PolygonPlotter2` calls `onPolygonChange` from the effect that loads `initialPolygonSet`, so
     * it fires on every entity open — with the very array it was handed. Marking dirty
     * unconditionally therefore armed the leave guard the moment an operator so much as LOOKED at a
     * machine, which is the one thing AC10 forbids in as many words: "a guard that always warns is
     * one operators learn to dismiss". The rig that walked AC10 missed it because it made a real
     * edit first, so the guard would have fired either way.
     *
     * The reference test is exact rather than heuristic: the plotter echoes the very array it was
     * seeded with, and this handler is the one from the render that seeded it, so `initialPolygons`
     * IS that array. (It used to compare against `polygonsByEntity[refid]`, which stopped being the
     * seed once the seed became one camera's regions rather than the entity's.)
     *
     * The empty clause asks what the SESSION holds on this camera, not what the seed was. It used to
     * be `initialPolygons.length === 0 && polygons.length === 0`, which read *Clear Observer* (or
     * deleting the only region) after a first draw on a camera as the plotter seeding itself — the
     * seed was `[]` — so the region stayed in the session, off the picture, and Save filed it
     * (`camera-first-observers`, found by the R10a test). An empty report against an empty camera
     * is a no-op either way.
     *
     * A seed changes nothing, so nothing below runs for one: it is the state the session already
     * holds, handed back.
     */
    // What the plotter held before this report: the open entity's regions on the camera shown.
    const previous = polygonsOnCamera(
      targetId,
      polygonsByEntity[targetId] ?? [],
      observerState,
      selectedEndpointId,
    );
    const isPlotterSeeding =
      polygons === initialPolygons || (polygons.length === 0 && previous.length === 0);
    if (isPlotterSeeding) return;
    /*
     * One undo step per change to the FINISHED regions (FR16): a region closed, a corner moved or
     * added, a region deleted. A drag is one step, not one per pointer move — the drawing area brackets
     * it with `onEditGesture`, and only its first report records. The points of a region still being
     * drawn are not an edit until it closes, so they record nothing.
     */
    {
      const before = new Map(
        previous.filter(p => p.isComplete).map(p => [p.id, JSON.stringify(p.points)]),
      );
      const now = polygons.filter(p => p.isComplete);
      const added = now.some(p => !before.has(p.id));
      const removed = Array.from(before.keys()).some(id => !now.some(p => p.id === id));
      const reshaped = now.some(
        p => before.has(p.id) && before.get(p.id) !== JSON.stringify(p.points),
      );
      if (added || removed || reshaped) {
        const gesture = editGestureRef.current;
        if (!gesture.active || !gesture.recorded) {
          recordUndo({
            kind: added ? 'new' : removed ? 'deleted' : 'reshaped',
            entity: selected.label,
          });
          if (gesture.active) gesture.recorded = true;
        }
        if (added) setRegionClosedHere(true);
      }
    }
    /*
     * ⚠ MERGE, never replace. The plotter holds only the camera shown, so writing its set over the
     * entity would delete every region on every other camera at the first edit — and Save would
     * then file the entity without them (`multi-camera-observer-view`, R7).
     *
     * Report is derived in an effect off the resulting state, so this updater stays pure (no side
     * effects that could double-fire under Strict Mode).
     */
    const offCamera = (p: Polygon) => !isOnCamera(targetId, p, observerState, selectedEndpointId);
    setPolygonsByEntity(prev => ({
      ...prev,
      [targetId]: mergeCameraPolygons(prev[targetId] ?? [], polygons, offCamera),
    }));

    /*
     * A newly-drawn polygon needs its own state entry, on the camera it was drawn on — otherwise
     * the observer that follows would have no camera, which is the per-entity behaviour this
     * replaces arriving by the back door.
     */
    const known = new Set(Object.keys(observerState));
    const fresh = polygons.filter(p => p.isComplete && !known.has(observerRefid(targetId, p.id)));
    /*
     * The draw-time stamp (`camera-observer-setup-attention`, FR3/FR11): the entity as it stands
     * NOW is what an observer committed now was drawn against. Stamped for a FRESH polygon and for
     * an existing one whose region changed — redrawing a flagged region over the machine's new
     * position is the obvious response to *Observer needs checking*, and it must clear the flag
     * without a second control. The plotter's own seed is not an edit, so it stamps nothing.
     */
    {
      const previousPoints = new Map(previous.map(p => [p.id, JSON.stringify(p.points)]));
      const edited = polygons.filter(
        p =>
          p.isComplete &&
          (!known.has(observerRefid(targetId, p.id)) ||
            previousPoints.get(p.id) !== JSON.stringify(p.points)),
      );
      stampObservers(
        edited.map(p => observerRefid(targetId, p.id)),
        geometryOf(sessionLayout, targetId),
      );
    }
    if (fresh.length > 0) {
      setObserverState(prev => {
        const next = { ...prev };
        for (const p of fresh) {
          next[observerRefid(targetId, p.id)] = {
            endpointId: selectedEndpointId,
            trackSide: newRegionTrackSide,
          };
        }
        return next;
      });
      // Select the one just finished, so the track-side control acts on it.
      setSelectedObserverId(observerRefid(targetId, fresh[fresh.length - 1].id));
      // The fresh observer owns the camera the entity was being drawn on; the holder has done its
      // job. Clearing it keeps `selectedEndpointId` reading from the observer from here on.
      setPendingEndpointId(null);
    }

    /*
     * Re-point the selection when the observer it names has just been deleted, and drop its state.
     *
     * Without this the strip has no tile matching `selectedObserverId`, so every tile takes
     * `tabIndex={-1}` and the strip stops being reachable by Tab at all — and `selectedEndpointId`
     * keeps resolving through the dead entry, so the plotter shows a deleted observer's camera.
     */
    if (selectedObserverId) {
      /*
       * The selected observer is always on the camera shown, so this only ever fires for a region
       * deleted here. The re-point prefers a survivor on this camera; with none left, the camera is
       * held as PENDING so the view stays on it and the operator can draw again (R10). Without that
       * the shown camera — derived from the selected observer — would fall to `null`, and the
       * plotter would remount onto no picture at all.
       */
      const onThisCamera = polygons
        .filter(p => p.isComplete)
        .map(p => observerRefid(targetId, p.id));
      if (!onThisCamera.includes(selectedObserverId)) {
        setSelectedObserverId(onThisCamera[0] ?? null);
        if (!onThisCamera[0]) setPendingEndpointId(selectedEndpointId);
        setObserverState(prev => {
          const next = { ...prev };
          delete next[selectedObserverId];
          return next;
        });
      }
    }

    markPlanDirty();
  };

  /*
   * An entity's observers that sit on NO camera, given one (FR21, ruling R2): a camera picked from
   * the linking suggestions moves them onto it — one edit, one undo step — and that camera comes on
   * the stage. Moving an observer that already HAS a camera is not offered anywhere: its region is in
   * that camera's picture coordinates.
   */
  const linkUnlinkedToCamera = (entity: SelectedEntity, endpointId: number) => {
    const unlinked = (polygonsByEntity[entity.refid] ?? [])
      .filter(p => p.isComplete && endpointOfRegion(entity.refid, p, observerState) === null)
      .map(p => observerRefid(entity.refid, p.id));
    if (unlinked.length > 0) {
      recordUndo({
        kind: 'linkedToCamera',
        entity: entity.label,
        camera: cameraNameOf(endpointId),
      });
      setObserverState(prev => {
        const next = { ...prev };
        for (const refid of unlinked) {
          next[refid] = { endpointId, trackSide: prev[refid]?.trackSide ?? DEFAULT_TRACK_SIDE };
        }
        return next;
      });
      stampObservers(unlinked, geometryOf(sessionLayout, entity.refid));
      markPlanDirty();
    }
    openEditor(entity.refid, entity.kind, endpointId, {
      from: { view: 'side-by-side', refid: entity.refid, kind: entity.kind },
    });
    // The regions are on that camera from this commit on: open on them, the first selected.
    setSelectedObserverId(unlinked[0] ?? null);
    setPendingEndpointId(unlinked[0] ? null : endpointId);
  };

  /*
   * What the live read has to say — rendered above BOTH branches below, including the "nothing on
   * the plan yet" gate. A twin whose observer read just failed needs to be told so whether or not
   * its plan happens to be empty: those are two different problems, and showing only the second
   * would be the same conflation this feature exists to remove.
   */
  /** The plan's own observers across current designs — what the draft sentence counts (FR10). */
  const planObserverCount = layoutCurrentDesigns(layout ?? null).reduce(
    (n, design) => n + asArray(design?.observers).filter(isLive).length,
    0,
  );
  /*
   * How the notices name a code (`onboarding-polish-observer-notices`, R1–R2): the plan, then the
   * asset register, then the twin that is live now, then the observer's stored name. Rebuilt only
   * when one of those changes.
   */
  const modelLayout = model?.status === 'ready' ? model.value : null;
  // The twin's asset register, as the page loaded it — the names equipment carries off the plan.
  const registerRows = useEquipmentCatalogStore(s => s.registerRows);
  const nameFor = useMemo(
    () =>
      buildEntityNamer({
        plan: sessionLayout,
        register: registerRows,
        model: modelLayout,
        twinBid: twinBidParam,
      }),
    [sessionLayout, registerRows, modelLayout, twinBidParam],
  );
  const noticesBody = liveConfiguration ? (
    <LiveConfigurationNotices
      className="stage-notices"
      streams={liveConfiguration.streams}
      config={liveConfiguration.config}
      observers={liveConfiguration.observers}
      model={liveConfiguration.model}
      match={liveConfiguration.match}
      compositeMatch={liveConfiguration.compositeMatch}
      onRetry={liveConfiguration.retry}
      observerSource={liveConfiguration.observerSource}
      draftObserverCount={planObserverCount}
      nameFor={nameFor}
    />
  ) : null;
  // In the one-bar header's chip when it has a slot (L2); above the stage as before otherwise.
  const notices =
    noticesBody && noticesSlot
      ? createPortal(<NoticesChip launchNote={launchNote}>{noticesBody}</NoticesChip>, noticesSlot)
      : noticesBody;

  // ── The stage (`observers-page-stage`) ───────────────────────────────────────────────────────

  const toast = useToast();
  /** The session as it stands NOW — Undo runs long after the render that offered it. */
  const sessionNowRef = useRef({ polygonsByEntity, observerState });
  sessionNowRef.current = { polygonsByEntity, observerState };

  /**
   * Each floor with how many of its entities need attention — the same classification the list's
   * summary splits by floor (`attentionSummary`), counted per floor rather than this-or-other.
   */
  // ⚠ Counted only once the observer read has answered, as the dots and the list's Banner are: on a
  // failed read every entity classifies as having no observer, and a pill would claim so.
  const stageFloors = useMemo<StageFloor[]>(() => {
    const counts = new Map<number, number>();
    // Of those, the ones that need checking: the floor's pill is a problem when any does (R7).
    const checking = new Map<number, number>();
    for (const [refid, a] of observersStatus === 'ready' ? Object.entries(attentionByRefid) : []) {
      if (!NEEDS_ATTENTION.has(a.state)) continue;
      if (a.state === 'check' && modelStatus === 'failed') continue;
      const at = floorEntityIndex.get(refid)?.floorIndex;
      if (at === undefined) continue;
      counts.set(at, (counts.get(at) ?? 0) + 1);
      if (a.state === 'check') checking.set(at, (checking.get(at) ?? 0) + 1);
    }
    return asArray(sessionLayout?.floors).map((floor, index) => ({
      index,
      name: floor?.name?.trim() || `Floor ${index + 1}`,
      attention: counts.get(index) ?? 0,
      checking: checking.get(index) ?? 0,
    }));
  }, [observersStatus, modelStatus, attentionByRefid, floorEntityIndex, sessionLayout]);

  /*
   * The plan's dots (FR20): a dot where an observer is missing or needs checking, none where the
   * entity is measured, so the gaps stand out. Nothing is drawn until the observer read has answered
   * — a dot on every entity of a failed read would claim none has an observer. A failed running-model
   * read cannot decide *needs checking*, so those dots go and the legend says why. Both plans read
   * these (`onboarding-status-colours-observers`, R8): a space shows its status as its own outline,
   * stroked in the role's mark with no fill of its own (the level fill under it is the 3D plan's), a
   * machine as a dot, and one added in this draft carries the added disc. The stage draws the picked
   * or hovered entity in the plan's selection colour (it owns the hover).
   */
  const markers = useMemo<CanvasMarker[]>(() => {
    if (observersStatus !== 'ready') return [];
    const out: CanvasMarker[] = [];
    const mark = (refid: string, shape: 'outline' | 'dot') => {
      const state = attentionByRefid[refid]?.state;
      if (!state || (state === 'check' && modelStatus === 'failed')) return;
      const color = STATUS_DOT[state];
      if (!color) return;
      out.push({
        id: refid,
        color,
        ...(shape === 'outline' ? { shape } : {}),
        ...(state === 'added-unobserved' ? { change: 'added' as const } : {}),
      });
    };
    for (const surface of surfaces) mark(surface.refid, 'outline');
    for (const item of items) mark(item.refid, 'dot');
    return out;
  }, [observersStatus, surfaces, items, attentionByRefid, modelStatus]);

  /** What the plan's hover card says: the entity, its code, and its group (FR20). */
  const describeHover = (refid: string): StageHoverText | null => {
    const label = entityLabels.get(refid);
    if (!label) return null;
    const state = attentionByRefid[refid]?.state;
    const look = state && state !== 'pending' ? GROUP_LOOK[state] : undefined;
    return {
      title: label.label,
      code: label.code,
      status: look ? { label: look.title, tone: look.tone } : undefined,
    };
  };

  /** The picked entity's camera groups — side by side, and the header's counts. */
  const stagePickRefid = stagePick?.refid ?? null;
  const stageGroups = useMemo(
    () =>
      stagePickRefid
        ? observerCameraGroups(
            stagePickRefid,
            polygonsByEntity[stagePickRefid] ?? [],
            observerState,
          )
        : [],
    [stagePickRefid, polygonsByEntity, observerState],
  );
  const stageEntity = stagePick
    ? {
        refid: stagePick.refid,
        label: stagePick.label,
        code: entityLabels.get(stagePick.refid)?.code,
        kind: stagePick.kind,
        observerCount: observerCounts[stagePick.refid] ?? 0,
        cameraCount: stageGroups.filter(g => g.endpointId !== null).length,
      }
    : null;
  /** The picked entity's cameras, scored — the suggestions and their captions (FR22). */
  const stageScores = useMemo(
    () => (stagePickRefid && layout ? scoreFeedsForEntity(layout, stagePickRefid, feeds) : []),
    [stagePickRefid, layout, feeds],
  );
  /** What the stage shows for what it was asked (`resolveStageView`): decided now, not at the pick. */
  const hasSuggestions = stageScores.length > 0;
  /*
   * One camera on stage is the camera view, whatever the stage was showing when it opened — that is
   * kept in `stage` (and `cameraFrom`), and is where the camera goes back to.
   */
  const shownStage: StageView = editorOpen
    ? {
        view: 'camera',
        endpointId: selectedEndpointId ?? -1,
        refid: selected?.refid ?? null,
        kind: selected?.kind,
        from: cameraFrom,
      }
    : resolveStageView(stage, {
        groupCount: stageGroups.length,
        hasSuggestions,
        feedsKnown: streamsStatus === 'ready',
      });
  editorOpenRef.current = editorOpen;
  undoNowRef.current = {
    endpointId: selectedEndpointId,
    selectedObserverId,
    waiting: !!waitingRegion,
  };
  /** Each observer's filed name for the side-by-side captions — the one producer of that text. */
  const stagePickCount = stagePickRefid ? (observerCounts[stagePickRefid] ?? 0) : 0;
  const stageTipFor = useCallback(
    (_refid: string, polygon: Polygon) =>
      tipForObserver(stagePickRefid ?? '', polygon.id, stagePickCount),
    [tipForObserver, stagePickRefid, stagePickCount],
  );

  /**
   * A camera chosen on the stage comes onto it (FR1). From the linking suggestions it is the camera
   * the entity's unlinked regions are moved onto (FR21); the unlinked card's own *Edit* opens those
   * suggestions.
   */
  const openFromStage = (endpointId: number | null) => {
    if (!stagePick || stage.view === 'plan' || stage.view === 'camera') return;
    if (endpointId == null) {
      setStage({
        view: 'suggestions',
        refid: stagePick.refid,
        kind: stagePick.kind,
        linking: true,
      });
      return;
    }
    if (stage.linking) {
      linkUnlinkedToCamera(stagePick, endpointId);
      return;
    }
    openEditor(stagePick.refid, stagePick.kind, endpointId);
  };
  // Stable, so the corner plan's canvas does not re-render with every stage render.
  const planBack = useCallback(() => setStage(PLAN_VIEW), []);
  const addCameraFromStage = () => {
    if (!stagePick) return;
    if (editorOpen) {
      addCameraFromCamera();
      return;
    }
    setStage({
      view: 'suggestions',
      refid: stagePick.refid,
      kind: stagePick.kind,
      adding: true,
    });
  };

  /*
   * *Remove Camera* (FR27): a confirm that names what goes and what stays, then the regions go —
   * an edit like any other, so the draft saves it — and a toast offers Undo. This replaces the
   * editor's refusal to remove a camera that still had regions.
   */
  /** After a removal: the place of the removed tile, so focus lands on the one that followed it. */
  const stageFocusRef = useRef<number | null>(null);
  /** The control that asked, so *Keep Camera* (or Esc) hands focus back to it. */
  const removeOpenerRef = useRef<HTMLElement | null>(null);
  const requestRemove = (endpointId: number | null) => {
    if (!stagePick) return;
    const opener = document.activeElement;
    removeOpenerRef.current = opener instanceof HTMLElement ? opener : null;
    setRemoveTarget({ entity: stagePick, endpointId });
  };
  /*
   * ⚠ A removal's toast lives no longer than the step. The provider is wizard-level and re-homes a
   * toast whose region unmounts, so without this an Undo pressed on step 6 would run this step's
   * closure after it was gone: nothing restored, yet the plan marked dirty with an edit pending
   * that no write-through will ever clear.
   */
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const stageMountedRef = useRef(true);
  useEffect(() => {
    stageMountedRef.current = true;
    return () => {
      stageMountedRef.current = false;
      clearHistory();
    };
  }, []);
  const confirmRemove = () => {
    if (!removeTarget) return;
    const { entity, endpointId } = removeTarget;
    const what = endpointId == null ? 'Regions not linked to a camera' : cameraNameOf(endpointId);
    recordUndo(
      { kind: 'removedCamera', entity: entity.label, camera: what },
      { stage: { view: 'side-by-side', refid: entity.refid, kind: entity.kind }, pick: entity },
    );
    const out = removeEntityFromCamera(polygonsByEntity, observerState, entity.refid, endpointId);
    setPolygonsByEntity(out.polygonsByEntity);
    setObserverState(out.observerState);
    markPlanDirty();
    setRemoveTarget(null);
    const left = observerCameraGroups(
      entity.refid,
      out.polygonsByEntity[entity.refid] ?? [],
      out.observerState,
    ).length;
    // Wherever the entity now stands — never the editor, unasked.
    setStage({
      view: left === 0 ? 'suggestions' : 'side-by-side',
      refid: entity.refid,
      kind: entity.kind,
    });
    const tiles = Array.from(
      stageBodyRef.current?.querySelectorAll<HTMLElement>('.stage li[data-endpoint]') ?? [],
    );
    stageFocusRef.current = Math.max(
      0,
      tiles.findIndex(li => li.dataset.endpoint === String(endpointId ?? 'none')),
    );
    const id: string = toast.show({
      tone: 'neutral',
      icon: <Icon name="video-slash" />,
      title: `${what} removed from ${entity.label}`,
      inlineAction: { label: 'Undo', onAction: () => undoFromToast(id) },
      region: STAGE_TOAST_REGION,
    });
    attachToast(id);
  };
  /** The first control a stage view offers: a tile, else *Add Camera*, else the plan, else the body. */
  const stageEntryPoint = (from = 0): HTMLElement | null => {
    const body = stageBodyRef.current;
    if (!body) return null;
    const tiles = Array.from(body.querySelectorAll<HTMLElement>('.stage li[data-endpoint]'))
      .map(li => li.querySelector<HTMLElement>('button, [role="button"]'))
      .filter((b): b is HTMLElement => !!b);
    return (
      tiles[Math.min(from, tiles.length - 1)] ??
      body.querySelector<HTMLElement>('.stage [data-add-camera]') ??
      body.querySelector<HTMLElement>('.stage [tabindex="0"][aria-label^="Floor plan"]') ??
      body
    );
  };
  // After a removal the tile that held focus is gone: focus the one that followed it (FR27).
  useEffect(() => {
    if (stageFocusRef.current === null) return;
    const at = stageFocusRef.current;
    stageFocusRef.current = null;
    stageEntryPoint(at)?.focus();
  });
  /*
   * Any other change of view that took the focused control with it — the corner plan, P or Esc, Add
   * Camera, Back to all cameras — would drop focus to the page: it lands on the new view instead.
   * Only then: focus left in the list, or anywhere else still on the page, stays where it is.
   */
  const shownStageKey = `${shownStage.view}:${shownStage.view === 'plan' ? '' : shownStage.refid}`;
  useEffect(() => {
    // One camera on stage: the drawing area's own hand-off places focus on its canvas.
    if (shownStage.view === 'camera') return;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    stageEntryPoint()?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownStageKey]);

  /*
   * P, R and Esc on the step (FR29). One listener; it stands down while any dialog is open — the
   * editor's own Esc and its plotter's keys answer there — and in any field being typed in.
   */
  /*
   * On the camera (FR18) the drawing area answers first, on its own container, and stops what it
   * consumed (an open region's Esc, Ctrl+Z and Backspace; Delete on a region it selected; zoom and pan).
   * This answers the rest: Esc one level per press, Ctrl+Z the page's undo, Delete the selected
   * observer, R to recentre the picture, ? the shortcut card, P the plan.
   */
  const cameraKeysRef = useRef<{
    /** A region is open on the picture: Ctrl+Z is its Undo Point and Esc its Cancel (FR18). */
    openRegion: boolean;
    /** *Add Observer* has armed a region: Esc's first level cancels it (R4.3). */
    armed: boolean;
    undoPoint: () => void;
    cancelRegion: () => void;
    escape: () => void;
    deleteSelected: () => boolean;
    recentre: () => void;
    toggleShortcuts: () => void;
    plan: () => void;
  } | null>(null);
  cameraKeysRef.current = editorOpen
    ? {
        openRegion: drawingPoints > 0,
        armed: addObserverArmed,
        // As the bar's own buttons do: if the key takes the last point, the bar leaves, and focus
        // (perhaps on one of its buttons) goes back to the picture rather than the page.
        undoPoint: () => {
          cameraFocusPendingRef.current = true;
          plotterRef.current?.undoLastPoint();
        },
        cancelRegion: () => {
          cameraFocusPendingRef.current = true;
          plotterRef.current?.cancelRegion();
        },
        escape: () => {
          const step = cameraStepBack({
            armed: addObserverArmed,
            shortcutsOpen,
            waiting: !!waitingRegion,
            selectedObserver: !!selectedObserverId,
            pickedInCameraByCamera: !!workspace && !!selected,
          });
          if (step === 'disarm') {
            setAddObserverArmed(false);
            say(ADD_OBSERVER_CANCELLED);
          } else if (step === 'close-shortcuts') setShortcutsOpen(false);
          else if (step === 'discard-waiting') {
            setWaitingRegion(null);
            setCameraEntry(n => n + 1);
            // Rides in front of the next line, so the change of view does not write over it.
            statusNoteRef.current =
              "The region that was waiting wasn't linked, so it was discarded.";
          } else if (step === 'deselect-observer') {
            setPendingEndpointId(selectedEndpointId);
            setSelectedObserverId(null);
          } else if (step === 'clear-pick') deselect();
          else leaveCamera();
        },
        deleteSelected: () => {
          if (!selected || !selectedObserverId) return false;
          deleteObserver(selectedObserverId);
          return true;
        },
        recentre: () => plotterRef.current?.recentre(),
        toggleShortcuts: () => setShortcutsOpen(o => !o),
        plan: () => leaveCamera(PLAN_VIEW),
      }
    : null;
  const pageUndoRef = useRef(undo);
  pageUndoRef.current = undo;
  const keyStateRef = useRef({ stage: shownStage, stagePick, hasSuggestions });
  keyStateRef.current = { stage: shownStage, stagePick, hasSuggestions };
  /*
   * Whether the step was the last place the operator was. Focus can fall to the page (a control
   * that held it unmounted, or a click on something that takes no focus); a key pressed then belongs
   * to the step only if the step had it last — never after a press or a focus elsewhere on the page.
   */
  const lastInStepRef = useRef(true);
  useEffect(() => {
    const note = (e: Event) => {
      const t = e.target instanceof Node ? e.target : null;
      if (!t || t === document.body || t === document.documentElement) {
        if (e.type === 'pointerdown') lastInStepRef.current = false;
        return;
      }
      lastInStepRef.current = !!stageBodyRef.current?.contains(t);
    };
    document.addEventListener('focusin', note);
    document.addEventListener('pointerdown', note, true);
    return () => {
      document.removeEventListener('focusin', note);
      document.removeEventListener('pointerdown', note, true);
    };
  }, []);
  /** The walk-through's keys (`observer-autogen-review-walkthrough`, R3.1, R4): set each render. */
  const reviewKeysRef = useRef<{
    next: (direction: 1 | -1) => void;
    use: () => void;
    keep: () => void;
    adjust: () => void;
    relink: () => void;
    discard: () => void;
  } | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (document.querySelector('[role="dialog"]')) return;
      // The step's keys act only from inside the step, or with focus nowhere at all (FR18).
      const inStep =
        !target || target === document.body
          ? lastInStepRef.current
          : !!stageBodyRef.current?.contains(target);
      if (!inStep) return;
      const camera = cameraKeysRef.current;
      /*
       * A region still open answers first. The drawing area consumes these keys itself on the
       * canvas, but not from a control laid over the picture (a card's button), so they arrive here:
       * Ctrl+Z is still Undo Point and Esc still Cancel, never the page's undo or leaving the camera.
       */
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (camera?.openRegion) camera.undoPoint();
        else pageUndoRef.current();
        return;
      }
      if (e.ctrlKey || e.metaKey) return;
      // The stage's space keys work on every view (`observer-autogen-review-layout`, L4.2). Inputs,
      // dialogs and keys from outside the step never reach here (the guards above).
      if (e.key === '[') {
        e.preventDefault();
        toggleListRef.current();
        return;
      }
      const now = keyStateRef.current;
      const review = reviewKeysRef.current;
      if (camera && review && !camera.openRegion) {
        const k = e.key.toLowerCase();
        const act: Record<string, () => void> = {
          n: () => review.next(e.shiftKey ? -1 : 1),
          a: review.use,
          k: review.keep,
          e: review.adjust,
          l: review.relink,
          d: review.discard,
        };
        if (act[k]) {
          e.preventDefault();
          act[k]();
          return;
        }
      }
      if (camera) {
        if (e.key === 'Escape') {
          e.preventDefault();
          if (camera.openRegion) camera.cancelRegion();
          else camera.escape();
        } else if (e.key === 'Delete') {
          if (camera.deleteSelected()) e.preventDefault();
        } else if (e.key === 'r' || e.key === 'R') {
          e.preventDefault();
          camera.recentre();
        } else if (e.key === '?') {
          e.preventDefault();
          camera.toggleShortcuts();
        } else if (e.key === 'p' || e.key === 'P') {
          e.preventDefault();
          camera.plan();
        }
        return;
      }
      if (e.key === 'p' || e.key === 'P') {
        if (now.stage.view === 'plan') return;
        e.preventDefault();
        setStage(PLAN_VIEW);
      } else if (e.key === 'r' || e.key === 'R') {
        // Wherever the plan is drawn: the plan view and under the likely cameras' panel (R5.4).
        const v = now.stage.view;
        if (v !== 'plan' && v !== 'suggestions' && v !== 'all-cameras') return;
        e.preventDefault();
        setRecentreSignal(n => n + 1);
      } else if (e.key === 'Escape') {
        const back = stepBack(now.stage, !!now.stagePick, now.hasSuggestions);
        if (!back) return;
        e.preventDefault();
        setStage(back.view);
        if (back.clearSelection) setStagePick(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /*
   * The wizard's step list gives the picture its width whenever cameras are on the stage (FR5) —
   * asked for on a change, never on arrival: the step opens with the list as it was.
   */
  const camerasOnStage = shownStage.view !== 'plan';
  const onNavCollapseWantedRef = useRef(onNavCollapseWanted);
  onNavCollapseWantedRef.current = onNavCollapseWanted;
  const navAskedRef = useRef(camerasOnStage);
  useEffect(() => {
    if (navAskedRef.current === camerasOnStage) return;
    navAskedRef.current = camerasOnStage;
    onNavCollapseWantedRef.current?.(camerasOnStage);
  }, [camerasOnStage]);

  /** What the confirm says will go, and what stays (FR27). */
  const removeCopy = (() => {
    if (!removeTarget) return null;
    const { entity, endpointId } = removeTarget;
    const groups = observerCameraGroups(
      entity.refid,
      polygonsByEntity[entity.refid] ?? [],
      observerState,
    );
    const count = observerCounts[entity.refid] ?? 0;
    const going = groups.find(g => g.endpointId === endpointId)?.observers ?? [];
    const staying = groups.filter(g => g.endpointId !== endpointId);
    const kept = staying.reduce((n, g) => n + g.observers.length, 0);
    const camera = endpointId == null ? 'regions not linked to a camera' : cameraNameOf(endpointId);
    const names = staying
      .map(g => (g.endpointId == null ? 'no camera' : cameraNameOf(g.endpointId)))
      .join(', ');
    return {
      title:
        endpointId == null
          ? `Remove ${entity.label}'s regions not linked to a camera?`
          : `Remove ${camera} from ${entity.label}?`,
      lead: `This deletes ${entity.label}'s region${going.length === 1 ? '' : 's'} ${
        endpointId == null ? 'not linked to a camera' : 'on this camera'
      }.`,
      regions: going.map(o => ({
        refid: o.refid,
        title: tipForObserver(entity.refid, o.polygon.id, count).title,
      })),
      code: entityLabels.get(entity.refid)?.code,
      after:
        kept > 0
          ? `${entity.label} keeps its ${kept} region${kept === 1 ? '' : 's'} on ${names}.${
              endpointId == null ? '' : ` Other entities on ${camera} are not affected.`
            }`
          : `${entity.label} will have no observer.${
              endpointId == null ? '' : ` Other entities on ${camera} are not affected.`
            }`,
    };
  })();

  /*
   * *Delete Observer* and the Delete key (FR15): at once, no confirm — one undo step, and a neutral
   * toast that offers it. The selection moves to a survivor on this camera, or the camera is held.
   */
  const deleteObserver = (refidToDelete: string) => {
    if (!selected) return;
    const refid = selected.refid;
    const polygonId = polygonIdFromObserverRefid(refidToDelete, refid);
    const own = polygonsByEntity[refid] ?? [];
    if (!own.some(p => p.id === polygonId)) return;
    const name = tipForObserver(refid, polygonId, entityObserverCount).title;
    recordUndo({ kind: 'deleted', entity: selected.label });
    const rest = own.filter(p => p.id !== polygonId);
    setPolygonsByEntity(prev => ({ ...prev, [refid]: rest }));
    setObserverState(prev => {
      const next = { ...prev };
      delete next[refidToDelete];
      return next;
    });
    const survivor = rest.find(
      p => p.isComplete && endpointOfRegion(refid, p, observerState) === selectedEndpointId,
    );
    setSelectedObserverId(survivor ? observerRefid(refid, survivor.id) : null);
    // The last observer here takes Delete Observer with it: focus goes to the picture (Block A1).
    cameraFocusPendingRef.current = true;
    if (!survivor) {
      setPendingEndpointId(selectedEndpointId);
      setRegionClosedHere(false);
    }
    setCameraEntry(n => n + 1);
    markPlanDirty();
    const id: string = toast.show({
      tone: 'neutral',
      icon: <Icon name="trash-can" />,
      title: `${name} deleted`,
      inlineAction: { label: 'Undo', onAction: () => undoFromToast(id) },
      region: STAGE_TOAST_REGION,
    });
    attachToast(id);
  };

  /** The list row to bring into view after a pick on the picture (focus stays on the picture). */
  const scrollRowRef = useRef<string | null>(null);
  useEffect(() => {
    const refid = scrollRowRef.current;
    if (!refid) return;
    scrollRowRef.current = null;
    Array.from(stageBodyRef.current?.querySelectorAll<HTMLElement>('[data-refid]') ?? [])
      .find(el => el.getAttribute('data-refid') === refid)
      ?.scrollIntoView?.({ block: 'nearest' });
  });

  /** The region the drawing area has selected: the observer the cards act on (FR8, FR10). */
  const selectRegion = (polygonId: string | null) => {
    // Deselecting is `onClearSelection`'s; this is only ever a pick.
    if (!polygonId || !selected) return;
    const refid = observerRefid(selected.refid, polygonId);
    if (observerState[refid]) setSelectedObserverId(refid);
  };

  /** A region of another entity hovered in Edit: its list row shows its hover look (FR20). */
  const hoverRegionOnPicture = (observerRefidHovered: string | null) => {
    const target = observerRefidHovered
      ? Object.entries(polygonsByEntity).find(([targetRefid, polys]) =>
          polys.some(p => observerRefid(targetRefid, p.id) === observerRefidHovered),
        )?.[0]
      : undefined;
    pictureHoverStore.set(target ? { refid: target, at: { x: 0, y: 0 } } : null);
  };

  /*
   * A region of ANOTHER entity clicked in Edit (FR20; the backlog's "pick an entity by clicking its
   * region"): that entity is the pick — the camera stays, it has a region here — and its region is
   * selected, with its list row.
   */
  const pickRegionOnPicture = (observerRefidPicked: string) => {
    if (selectedEndpointId == null) return;
    for (const [targetRefid, polys] of Object.entries(polygonsByEntity)) {
      const polygon = polys.find(p => observerRefid(targetRefid, p.id) === observerRefidPicked);
      if (!polygon) continue;
      // From the twin-wide index: the backdrop holds every floor's entities on this camera.
      const kind: ObserverTargetKind =
        floorEntityIndex.get(targetRefid)?.kind ??
        (targetRefid.startsWith('floor:') ? 'floor' : 'item');
      if (workspace) {
        const linking = !!waitingRegion;
        pickInWorkspace(targetRefid, kind);
        // The pick linked the waiting region to it: nothing stays picked (R7.4).
        if (linking) return;
      } else openEditor(targetRefid, kind, selectedEndpointId);
      setSelectedObserverId(observerRefidPicked);
      scrollRowRef.current = targetRefid;
      return;
    }
  };

  // ── One camera on the stage (`observers-page-camera-stage`) ─────────────────────────────────
  const cameraName = selectedEndpointId != null ? cameraNameOf(selectedEndpointId) : 'Camera';
  const selectedTip =
    selected && selectedObserverId
      ? tipForObserver(
          selected.refid,
          polygonIdFromObserverRefid(selectedObserverId, selected.refid),
          entityObserverCount,
        )
      : null;
  const guide = stepGuideFor({
    pickedName: selected?.label ?? null,
    pickedRegionClosed: regionClosedHere,
    waiting: !!waitingRegion,
    selectedName: selectedTip?.title ?? null,
    regionOpen: drawingPoints > 0,
    hasRegionsHere: cameraObservers.length > 0,
  });
  const entityCount = selected ? (observerCounts[selected.refid] ?? 0) : 0;
  const onThisCameraCount = cameraObservers.length;
  const entityMeta =
    observersStatus !== 'ready'
      ? null
      : entityCount === 0
        ? 'No observer yet'
        : onThisCameraCount === 0
          ? 'No observer on this camera yet'
          : onThisCameraCount === entityCount
            ? `${onThisCameraCount} observer${onThisCameraCount === 1 ? '' : 's'} on this camera`
            : `${onThisCameraCount} of ${entityCount} observers on this camera`;
  const chips: EntityCameraChip[] = cameraGroups.map(g => ({
    endpointId: g.endpointId,
    label: g.endpointId == null ? 'Not linked' : cameraNameOf(g.endpointId),
    regionCount: g.observers.length,
  }));
  // A camera the entity has no region on yet still shows as its current chip (v3 state 03).
  if (selectedEndpointId != null && !chips.some(c => c.endpointId === selectedEndpointId)) {
    chips.push({ endpointId: selectedEndpointId, label: cameraName, regionCount: 0 });
  }
  const checkAttention =
    selected && attentionByRefid[selected.refid]?.state === 'check' && modelStatus !== 'failed'
      ? attentionByRefid[selected.refid]
      : undefined;
  /*
   * The tracking card waits for a pick only where one is due (`observer-unified-draw-edit`, R6): the
   * entity has regions here and none is selected. Drawing a new one — armed, or a region open — sets
   * what new regions track, as the card did in the retired Draw mode.
   */
  const trackingNeedsPick =
    !!selected &&
    onThisCameraCount > 0 &&
    !selectedObserverId &&
    drawingPoints === 0 &&
    !addObserverArmed;
  const trackTitle = trackingNeedsPick
    ? 'Pick an observer first'
    : selectedTip
      ? `${selectedTip.title} tracks`
      : waitingRegion
        ? 'This region tracks'
        : 'New regions track';
  // The same value the heat is read for (`heatTrackSide`), so the card and the heat cannot disagree.
  const trackValue: ObserverTrackSide = heatTrackSide;
  const changeTrackSide = (side: ObserverTrackSide) => {
    if (!selectedObserverId || !selected) {
      setNewRegionTrackSide(side);
      return;
    }
    if ((observerState[selectedObserverId]?.trackSide ?? DEFAULT_TRACK_SIDE) === side) return;
    recordUndo({ kind: 'trackSide', entity: selected.label });
    // Per OBSERVER, not per entity — two observers on one machine can track different keypoints.
    setObserverState(prev => ({
      ...prev,
      [selectedObserverId]: {
        endpointId: prev[selectedObserverId]?.endpointId ?? null,
        trackSide: side,
      },
    }));
    stampObservers([selectedObserverId], geometryOf(sessionLayout, selected.refid));
    markPlanDirty();
  };
  const observerStrip =
    selected && cameraObservers.length > 1 ? (
      <ObserverStrip
        role="group"
        aria-label={`Observers on ${selected.label} on this camera`}
        onKeyDown={e => {
          const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
          if (!keys.includes(e.key)) return;
          e.preventDefault();
          const at = cameraObservers.findIndex(o => o.id === selectedObserverId);
          const last = cameraObservers.length - 1;
          const next =
            e.key === 'Home'
              ? 0
              : e.key === 'End'
                ? last
                : at < 0
                  ? 0
                  : (at + (e.key === 'ArrowRight' ? 1 : -1) + cameraObservers.length) %
                    cameraObservers.length;
          setSelectedObserverId(cameraObservers[next].id);
          e.currentTarget.querySelectorAll<HTMLButtonElement>('button')[next]?.focus();
        }}
      >
        {cameraObservers.map(o => {
          const active = o.id === selectedObserverId;
          return (
            <Tag
              key={o.id}
              surface="on-glass"
              size="dense"
              toggle
              active={active}
              tabIndex={active ? 0 : -1}
              aria-label={`${o.tip.title}, ${o.tip.detail}`}
              onToggle={() => setSelectedObserverId(o.id)}
            >
              {o.tip.title}
            </Tag>
          );
        })}
      </ObserverStrip>
    ) : undefined;
  const cameraBanners = (
    <>
      {/* The messages ride the status strip over the picture, so one arriving mid-draw (a heat read
          failing) cannot move the picture (Mike, 2026-10-13; `observer-autogen-review-layout`). */}
      <MessageRow data-testid="observers-camera-messages">
        {observersStatus === 'failed' && (
          <Banner surface="on-glass" tone="error" title="This twin's observers could not be read.">
            Regions already drawn on this camera are not shown, so anything drawn now may duplicate
            one. Use Retry at the top of the step.
          </Banner>
        )}
        {frameFailure && (
          <Banner
            surface="on-glass"
            tone="error"
            title="This camera's picture could not be loaded."
          >
            {frameFailure}{' '}
            {selected && cameraObservers.length > 0
              ? 'The regions already on this camera are still loaded and still saved; they just cannot be drawn without the picture.'
              : 'Choose another camera from the Cameras list, or add an edge device to this one.'}
          </Banner>
        )}
        {(gridFailed || gridUnreadable) && (
          <Banner
            surface="on-glass"
            tone="error"
            role="alert"
            title="Heat history could not be read."
          >
            {gridFailed ? gridFailed.reason : HEAT_GRID_READ_REASONS.unreadable}.{' '}
            <Button type="button" variant="ghost" size="xs" onClick={retryGridHeat}>
              Retry
            </Button>
          </Banner>
        )}
      </MessageRow>
      {/* Shown, not said here: the stage's one region says it (`stageNote`). The heat read's
          outcome is said there too, and shown by the Heatmap switch's description (FR3). */}
      <SelectedFeedStatus data-camera-status="">
        <SettledText text={editorStatusQuiet ? '' : editorStatus} />
      </SelectedFeedStatus>
    </>
  );
  /*
   * The machine-first walk-through (`observer-autogen-review-walkthrough`): the engine's per-machine
   * view for the camera shown, with what this session changed laid over it. It replaces the list in
   * the navigator while Suggestions are on, and falls back to the list where the engine can't place
   * machines (R2.5).
   */
  const [selectedMachineRefid, setSelectedMachineRefid] = useState<string | null>(null);
  const [hoveredMachineRefid, setHoveredMachineRefid] = useState<string | null>(null);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  /** *Show every machine outline*: kept across cameras, as *Show every suggestion* is. */
  const [showAllOutlines, setShowAllOutlines] = useState(false);
  const [shownUnexplainedId, setShownUnexplainedId] = useState<string | null>(null);
  const [relinkingRefid, setRelinkingRefid] = useState<string | null>(null);
  const decisions = useSuggestionDecisionStore(st => st.decisions);
  const readyAnswer =
    typeof suggestionState === 'object' && suggestionState.status === 'ready'
      ? suggestionState.value
      : null;
  const reviewCamera =
    readyAnswer && selectedRtspBid
      ? (readyAnswer.cameras.find(c => c.rtspEndpointBid === selectedRtspBid) ?? null)
      : null;
  const review = useMemo(() => {
    if (!reviewCamera) return null;
    const machines = reviewCamera.result?.machines ?? [];
    const known = new Map(machines.map(m => [m.targetRefid, new Set(m.drawnObserverRefids)]));
    const drawn = machines.flatMap(m => m.drawnObserverRefids);
    const live = liveObserverState(polygonsByEntity, observerState);
    return reviewMachines(reviewCamera, {
      acceptedTargets: targetsWithNewObservers(live, selectedEndpointId, known),
      deletedDrawnRefids: new Set(drawn.filter(r => !(r in live))),
      decisions,
      takenProposalRefids: takenProposals(
        (reviewCamera.result?.proposals ?? []).map(p => ({
          refid: p.observer.refid,
          targetRefid: p.observer.targetRefid,
          points: p.observer.points,
        })),
        polygonsByEntity,
        observerState,
        selectedEndpointId,
      ),
    });
  }, [reviewCamera, polygonsByEntity, observerState, selectedEndpointId, decisions]);
  const reviewLoading = suggestionState === 'loading';
  const reviewError =
    typeof suggestionState === 'object' && suggestionState.status === 'failed'
      ? suggestionState.reason
      : null;
  const reviewActive =
    editorOpen &&
    suggestionsVisible &&
    !!selectedRtspBid &&
    (reviewLoading || !!reviewError || !!review);
  const reviewList = review && !review.fallback ? review.machines : [];
  const selectedMachine = reviewList.find(m => m.targetRefid === selectedMachineRefid) ?? null;

  // A new camera is a new list: nothing selected, nothing shown from the last one.
  useEffect(() => {
    setSelectedMachineRefid(null);
    setHoveredMachineRefid(null);
    setShownUnexplainedId(null);
    setRelinkingRefid(null);
  }, [selectedRtspBid]);

  /** What the drawing area shows while the walk-through is up: the machine under review (R3.2–R3.3). */
  const reviewOutlines = ((): SuggestionOutline[] | null => {
    if (!reviewActive || !review || review.fallback) return null;
    const outlines: SuggestionOutline[] = [];
    const seen = new Set<string>();
    const add = (m: ReviewMachine | undefined, faint: boolean) => {
      const p = m?.proposal;
      // A discarded suggestion is set aside: never drawn (spec `observer-autogen-review-discard-and-scores`, R1.3).
      if (!p || m?.discarded || p.observer.points.length < 3 || seen.has(p.observer.refid)) return;
      seen.add(p.observer.refid);
      outlines.push({
        id: p.observer.refid,
        points: p.observer.points,
        label: suggestionLabel(p),
        faint,
      });
    };
    add(selectedMachine ?? undefined, false);
    add(
      reviewList.find(m => m.targetRefid === hoveredMachineRefid),
      false,
    );
    if (showAllSuggestions) reviewList.forEach(m => add(m, true));
    const shown = review.unexplained.find(p => p.observer.refid === shownUnexplainedId);
    if (shown && shown.observer.points.length >= 3) {
      outlines.push({
        id: shown.observer.refid,
        points: shown.observer.points,
        label: 'Possible plan error',
      });
    }
    return outlines;
  })();

  /**
   * Where the engine thinks each machine is (`observer-matching-beyond-geometry-machine-outline`):
   * the selected machine's and the hovered (or keyboard-focused) row's outlines at full strength,
   * and with *Show every machine outline* on, every listed machine's faint. One entry per machine
   * and view, at its strongest. A discarded suggestion hides only the suggestion; the machine's
   * outline still draws.
   */
  const reviewMachineOutlines = ((): MachineOutline[] | undefined => {
    if (!reviewActive || !review || review.fallback) return undefined;
    const byId = new Map<string, MachineOutline>();
    const add = (m: ReviewMachine | undefined, faint: boolean) => {
      if (!m) return;
      for (const o of m.outlines) {
        const id = `${m.targetRefid}:${o.view}`;
        byId.set(id, {
          id,
          machine: m.targetRefid,
          points: o.points,
          name: m.name,
          faint: faint || undefined,
        });
      }
    };
    // Faint first, then the full ones replace theirs (a Map keeps each id where it first went).
    if (showAllOutlines) reviewList.forEach(m => add(m, true));
    add(selectedMachine ?? undefined, false);
    add(
      reviewList.find(m => m.targetRefid === hoveredMachineRefid),
      false,
    );
    return Array.from(byId.values());
  })();

  /** The box a machine is framed on: its suggestion and its regions on this camera (R3.1). */
  const machineBox = (m: ReviewMachine) => {
    const pts = [
      ...(m.proposal?.observer.points ?? []),
      ...(polygonsByEntity[m.targetRefid] ?? [])
        .filter(
          p =>
            p.isComplete &&
            observerState[observerRefid(m.targetRefid, p.id)]?.endpointId === selectedEndpointId,
        )
        .flatMap(p => p.points),
    ];
    // Nothing of it on this picture: frame where the plan puts it, its first outline that can be
    // drawn (spec `observer-matching-beyond-geometry-machine-outline`, R4). Only with a picture to
    // frame: with none, there is nothing to zoom and nothing is drawn, so it says so instead.
    const outline =
      pts.length === 0 && effectiveFrameDataUrl
        ? m.outlines.find(o => o.points.length >= 3)
        : undefined;
    const framed = outline ? outline.points : pts;
    if (framed.length === 0) return null;
    const xs = framed.map(p => p.x);
    const ys = framed.map(p => p.y);
    const x = Math.min(...xs);
    const y = Math.min(...ys);
    return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y, onOutline: !!outline };
  };

  /**
   * Pick a machine WITHOUT leaving the camera: the walk-through is this camera's list, so a machine
   * with no region here yet is picked on it, never sent to the stage's camera choice (R3.1).
   */
  const pickForReview = (refid: string) => {
    if (selected?.refid === refid) return;
    if (workspace) pickInWorkspace(refid, 'item');
    else openEditor(refid, 'item', selectedEndpointId);
  };

  const selectMachine = (m: ReviewMachine) => {
    setSelectedMachineRefid(m.targetRefid);
    setRelinkingRefid(null);
    pickForReview(m.targetRefid);
    const box = machineBox(m);
    if (box) {
      const { onOutline, ...rect } = box;
      plotterRef.current?.frameTo(rect);
      say(onOutline ? `Framed ${m.name}: where the plan puts it` : `Framed ${m.name}`);
    } else {
      // Nothing to frame on (no heat, no region here): never leave the last machine zoomed.
      plotterRef.current?.recentre();
      say(`Selected ${m.name}: nothing on this picture to frame`);
    }
  };

  /**
   * One shared path for every suggestion taken — Use, Adjust, Wrong machine and Accept all (R5.1):
   * one undo step, the session updated, the plan dirtied. The plan store and the draft saver do the
   * rest, exactly as for a drawn region; nothing here reaches `twin_sense_observer` (row 24).
   */
  const commitRegionForEntity = (
    items: { targetRefid: string; machine: ReviewMachine }[],
    change: ObserverChange,
  ): string[] => {
    if (items.length === 0 || selectedEndpointId == null) return [];
    recordUndo(change);
    let pbe = polygonsByEntity;
    let os = observerState;
    const refids: string[] = [];
    for (const it of items) {
      const p = it.machine.proposal;
      if (!p) continue;
      const next = applySuggestion(
        pbe,
        os,
        it.targetRefid,
        p.observer.points,
        p.observer.trackSide,
        selectedEndpointId,
      );
      pbe = next.polygonsByEntity;
      os = next.observerState;
      refids.push(next.observerRefid);
      stampObservers([next.observerRefid], geometryOf(sessionLayout, it.targetRefid));
    }
    setPolygonsByEntity(pbe);
    setObserverState(os);
    /*
     * ⚠ Off the camera's route, the camera on stage is DERIVED from the selected observer — and a
     * suggestion replaces the machine's region here, so that observer may just have gone. Re-point
     * the selection at the region that replaced it (or hold the camera), as the draw path does,
     * or the stage would lose its camera.
     */
    if (selectedObserverId && !(selectedObserverId in os)) {
      const target = selectedObserverId.slice(0, selectedObserverId.indexOf(':'));
      const replacement = refids.find(r => r.startsWith(`${target}:`)) ?? null;
      setSelectedObserverId(replacement);
      if (!replacement) setPendingEndpointId(selectedEndpointId);
    }
    setCameraEntry(n => n + 1);
    markPlanDirty();
    return refids;
  };

  // A discarded suggestion stays set aside until Ask again: no action may take it, by button or key
  // (spec `observer-autogen-review-discard-and-scores`, R1.3; Copilot, #2190).
  const takeSuggestion = (m: ReviewMachine) => {
    if (!m.proposal || m.proposalTaken || m.discarded) return;
    commitRegionForEntity([{ targetRefid: m.targetRefid, machine: m }], {
      kind: 'suggestionUsed',
      entity: m.name,
    });
    say(`Used suggestion for ${m.name}`);
  };
  const keepMine = (m: ReviewMachine) => {
    if (!m.proposal || m.drawnObserverRefids.length === 0 || m.discarded || !selectedRtspBid) {
      return;
    }
    useSuggestionDecisionStore.getState().keep({
      targetRefid: m.targetRefid,
      cameraBid: selectedRtspBid,
      trackSide: m.proposal.observer.trackSide,
      decidedAt: new Date().toISOString(),
    });
    say(`Kept your observer for ${m.name}`);
  };
  const forgetKeep = (m: ReviewMachine) => {
    if (!selectedRtspBid) return;
    useSuggestionDecisionStore.getState().forget(m.targetRefid, selectedRtspBid);
    say(`${m.name} will ask again`);
  };
  /** Discard (D): set a wrong suggestion aside (spec `observer-autogen-review-discard-and-scores`, R1). */
  const discardSuggestion = (m: ReviewMachine) => {
    if (!m.proposal || m.proposalTaken || m.discarded || !selectedRtspBid) return;
    if (m.proposal.observer.points.length === 0) return;
    const { centroid, size } = regionShape(m.proposal.observer.points);
    useSuggestionDecisionStore.getState().keep({
      targetRefid: m.targetRefid,
      cameraBid: selectedRtspBid,
      trackSide: m.proposal.observer.trackSide,
      decidedAt: new Date().toISOString(),
      kind: 'discard',
      centroid,
      size: Math.max(size, 1),
    });
    say(`Discarded suggestion for ${m.name}`);
  };
  const setDiscardReason = (m: ReviewMachine, reason: DiscardReason | null) => {
    if (!selectedRtspBid) return;
    useSuggestionDecisionStore.getState().setReason(m.targetRefid, selectedRtspBid, reason);
    say(reason ? `Reason: ${DISCARD_REASON_TEXT[reason]}` : 'Reason cleared');
  };
  /** Your drawn observers' part for the Heat score row, when they all share one (R2.2). */
  const drawnPartFor = (m: ReviewMachine): string | null => {
    const parts = new Set(
      m.drawnObserverRefids.map(r => observerState[r]?.trackSide).filter(Boolean),
    );
    return parts.size === 1 ? trackSideLabel(Array.from(parts)[0] as string) : null;
  };
  const adjustSuggestion = (m: ReviewMachine) => {
    if (!m.proposal || m.proposalTaken || m.discarded) return;
    const [refid] = commitRegionForEntity([{ targetRefid: m.targetRefid, machine: m }], {
      kind: 'suggestionUsed',
      entity: m.name,
    });
    if (!refid) return;
    pickForReview(m.targetRefid);
    setSelectedObserverId(refid);
    say(`Adjust the suggestion for ${m.name}`);
  };
  const relinkSuggestion = (m: ReviewMachine, targetRefid: string) => {
    if (!m.proposal || m.proposalTaken || m.discarded) return;
    const otherName =
      reviewList.find(x => x.targetRefid === targetRefid)?.name ?? labelFor(targetRefid, 'item');
    commitRegionForEntity([{ targetRefid, machine: m }], {
      kind: 'suggestionUsed',
      entity: otherName,
    });
    say(`${m.name} suggestion linked to ${otherName}`);
  };
  const relinkOptions = (m: ReviewMachine) => {
    const candidates = (m.proposal?.candidates ?? [])
      .filter(c => c.targetRefid !== m.targetRefid)
      .map(c => ({ refid: c.targetRefid, name: c.targetName ?? labelFor(c.targetRefid, 'item') }));
    const rest = reviewList
      .filter(
        x => x.targetRefid !== m.targetRefid && !candidates.some(c => c.refid === x.targetRefid),
      )
      .map(x => ({ refid: x.targetRefid, name: x.name }));
    return [...candidates, ...rest];
  };
  const acceptAll = (machines: ReviewMachine[]) => {
    if (machines.length === 0) return;
    commitRegionForEntity(
      machines.map(m => ({ targetRefid: m.targetRefid, machine: m })),
      { kind: 'suggestionsAccepted', count: machines.length },
    );
    say(`Accepted ${machines.length} confident suggestion${machines.length === 1 ? '' : 's'}`);
  };

  reviewKeysRef.current =
    reviewActive && review && !review.fallback
      ? {
          next: direction => {
            const m = nextUnsettled(review, selectedMachineRefid, direction);
            if (m) selectMachine(m);
          },
          use: () => {
            if (selectedMachine) takeSuggestion(selectedMachine);
          },
          keep: () => {
            if (selectedMachine) keepMine(selectedMachine);
          },
          adjust: () => {
            if (selectedMachine) adjustSuggestion(selectedMachine);
          },
          relink: () => {
            if (!selectedMachine || selectedMachine.discarded) return;
            const id = selectedMachine.targetRefid;
            setRelinkingRefid(r => (r === id ? null : id));
          },
          discard: () => {
            if (selectedMachine) discardSuggestion(selectedMachine);
          },
        }
      : null;

  // Said once, when the last machine on the camera settles (Block A).
  const doneRef = useRef<string | null>(null);
  const reviewSettled = review?.progress.settled ?? 0;
  const reviewInView = review?.progress.inView ?? 0;
  useEffect(() => {
    if (!review || review.fallback || !selectedRtspBid) return;
    const key = `${selectedRtspBid}:${reviewInView}`;
    if (reviewInView > 0 && reviewSettled === reviewInView) {
      if (doneRef.current !== key) say(`Camera done: all ${reviewInView} machines settled`);
      doneRef.current = key;
    } else if (doneRef.current === key) {
      doneRef.current = null;
    }
    // `say` and `review` are fresh each render; the effect keys on the progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewSettled, reviewInView, selectedRtspBid]);

  const cameraBody = editorOpen ? (
    <ObserverCameraView
      guide={guide}
      drawing={{
        points: drawingPoints,
        // The bar leaves with the last point: focus goes back to the picture, not the page.
        onUndoPoint: () => {
          cameraFocusPendingRef.current = true;
          plotterRef.current?.undoLastPoint();
        },
        onCancel: () => {
          cameraFocusPendingRef.current = true;
          plotterRef.current?.cancelRegion();
        },
      }}
      layers={{
        observers: {
          count: otherObserverPolygons.filter(p => p.isComplete).length,
          cameraName,
          visible: backdropVisible,
          onChange: setBackdropVisible,
        },
        heat: selectedRtspBid
          ? {
              description: heatLayerSubline,
              visible: heatVisible,
              onChange: handleToggleHeatmap,
              history: {
                window: heatWindow,
                onWindowChange: changeHeatWindow,
              },
              outlines: {
                on: heatOutlinesOn,
                onChange: setHeatOutlinesOn,
                edge: outlineEdge,
                onEdgeChange: setDraggingOutlineEdge,
                onEdgeCommit: edge => {
                  commitOutlineEdge(edge);
                  setDraggingOutlineEdge(null);
                  setOutlineEdgeCommits(n => n + 1);
                },
                separatePeaks,
                onSeparatePeaksChange: on => {
                  setSeparatePeaks(on);
                  setOutlineEdgeCommits(n => n + 1);
                },
                // Cells that cannot be decoded, or a failed read; a heat image that will not paint
                // still leaves the cells, which is all the outlines need.
                unavailable:
                  gridCells === 'unreadable' || gridFailed
                    ? 'The heat could not be read, so there is nothing to outline'
                    : gridCells
                      ? undefined
                      : gridSettled?.status === 'ready'
                        ? 'No heat recorded for this part and window, so there is nothing to outline'
                        : 'Heat is loading',
              },
            }
          : null,
        suggestions:
          selectedRtspBid && planId !== null && suggestions
            ? {
                visible: suggestionsVisible,
                onChange: changeSuggestionsVisible,
                description: suggestionsVisible ? suggestions.description : 'Off',
                status: suggestions.statusText,
                warnings: suggestions.warnings,
                action: suggestions.action
                  ? { kind: suggestions.action, onClick: rereadSuggestions }
                  : null,
              }
            : null,
      }}
      banners={cameraBanners}
      camera={{
        name: cameraName,
        pictureTime:
          selectedFeed?.imageUrl && selectedFeed.capturedAt
            ? clockTime(selectedFeed.capturedAt)
            : null,
        onPlan: () => leaveCamera(PLAN_VIEW),
      }}
      space={{ listOpen, onToggleList: toggleList }}
      picture={
        <PlotterHost ref={editorBodyRef}>
          <PolygonPlotter2
            ref={plotterRef}
            /* Keyed by the camera alone, in both routes: picking another entity on it, deselecting and
               linking keep the picture's zoom and pan. The seed reload resets polygons, not the view. */
            key={`camera:${selectedEndpointId ?? 'none'}`}
            imgSrc={effectiveFrameDataUrl}
            /* Nothing picked: ONE region may wait, so the drawing area stops drawing once it holds one. */
            multiPolygon={workspace ? !!selected : true}
            initialPolygonSet={initialPolygons}
            framePolygons={otherObserverPolygons}
            framePolygonsVisible={backdropVisible}
            polygonTips={polygonTips}
            selectedRegionId={
              selectedObserverId && selected
                ? polygonIdFromObserverRefid(selectedObserverId, selected.refid)
                : null
            }
            suggestions={reviewOutlines ?? suggestions?.outlines}
            suggestionsVisible={suggestionsVisible}
            machineOutlines={reviewMachineOutlines}
            heatImage={
              gridShowsHeat && heatGridImage ? { ...heatGridImage, label: gridCaptionText } : null
            }
            onNaturalSize={reportPictureSize}
            heatmapLoading={heatmapLoading}
            heatmapVisible={heatVisible}
            heatOutlines={heatOutlines}
            onOutlineTaken={() => say('Outline added as a region')}
            onOutlineHighlight={(position, count) => say(`Heat outline ${position} of ${count}`)}
            emptyMessage={frameFailure ? NO_PICTURE_MESSAGE : undefined}
            regionsWaiting={!!workspace && !selected}
            /* Drawing is allowed here (`observer-unified-draw-edit`, R3.5): an entity is picked, or
               camera by camera with no region already waiting. */
            allowDraw={!!selected || (!!workspace && !waitingRegion)}
            drawArmed={addObserverArmed}
            // The first point is placed: the armed region is under way (R4.3).
            onDrawStart={() => {
              setAddObserverArmed(false);
              /*
               * A new region, not a redraw of the selected observer (R4.4, and Shift+click alike):
               * while it is drawn the tracking card sets what new regions track and Delete has no
               * observer to act on — the old one would otherwise take both.
               */
              if (selectedObserverId) {
                setPendingEndpointId(selectedEndpointId);
                setSelectedObserverId(null);
              }
            }}
            // A Tab+click selection, said with where it sits in the stack (R5, criterion 14).
            onStackStep={(regionId, position, count) => {
              const title = polygonTips[regionId]?.title;
              if (title) say(`${title}, ${position} of ${count} here`);
            }}
            onPickRegion={pickRegionOnPicture}
            onDeleteKey={polygonId =>
              selected && deleteObserver(observerRefid(selected.refid, polygonId))
            }
            onSelectRegion={selectRegion}
            onClearSelection={() => {
              // Edit's click on empty picture (FR20): nothing selected, the camera held.
              if (!selectedObserverId) return;
              setPendingEndpointId(selectedEndpointId);
              setSelectedObserverId(null);
            }}
            onHoverRegion={hoverRegionOnPicture}
            describedBy={shortcutsId}
            canvasLabel={`Picture from ${cameraName}`}
            onEditGesture={phase => {
              editGestureRef.current =
                phase === 'start'
                  ? { active: true, recorded: false }
                  : { active: false, recorded: false };
            }}
            onDrawingChange={setDrawingPoints}
            onZoomChange={pictureZoom.set}
            onPolygonChange={handlePolygonChange}
          />
          {(gridShowsHeat || gridMissing) && (
            // Which heat this is, and the days it does not cover yet — on the picture, so missing
            // days never pass for quiet ones (card 25). Said by the caption too; shown, not said here.
            <HeatChips aria-hidden="true">
              {gridShowsHeat && <span className="chip">{gridHeatChip(heatWindow, heatView)}</span>}
              {gridMissing && <span className="missing">{gridMissing}</span>}
            </HeatChips>
          )}
        </PlotterHost>
      }
      // Where this person left the cards over the picture (`observers-page-motion`): one layout per
      // person, in this browser, the same on every twin and camera — held by the camera view.
      cardsSub={user?.sub}
      announce={say}
      entityCard={
        selected
          ? grip => (
              <EntityCard
                grip={grip}
                kind={selected.kind}
                name={selected.label}
                code={entityLabels.get(selected.refid)?.code}
                meta={entityMeta}
                chips={chips}
                currentEndpointId={selectedEndpointId}
                onChip={endpointId => {
                  if (endpointId == null) {
                    leaveCamera({
                      view: 'suggestions',
                      refid: selected.refid,
                      kind: selected.kind,
                      linking: true,
                    });
                    return;
                  }
                  if (workspace) {
                    // Camera by camera the picture is the workspace's: the chip opens the entity's own.
                    openEditor(selected.refid, selected.kind, endpointId);
                    return;
                  }
                  editCamera(endpointId);
                }}
                onSideBySide={cameraGroups.length >= 2 ? sideBySideFromCamera : undefined}
                onAddCamera={addCameraFromCamera}
                // An entity is picked, so drawing is allowed here (R4.1).
                showAddObserver
                addObserverArmed={addObserverArmed}
                showDeleteObserver={onThisCameraCount > 0}
                canDelete={
                  !!selectedObserverId && cameraObservers.some(o => o.id === selectedObserverId)
                }
                onAddObserver={() => {
                  // A region is already being drawn: nothing to arm (it would outlive the region).
                  if (drawingPoints > 0) return;
                  // Pressed again: disarmed (R4.3).
                  if (addObserverArmed) {
                    setAddObserverArmed(false);
                    say(ADD_OBSERVER_CANCELLED);
                    return;
                  }
                  armAddObserver();
                }}
                onDeleteObserver={() => selectedObserverId && deleteObserver(selectedObserverId)}
                strip={observerStrip}
                check={
                  checkAttention
                    ? {
                        text: `${attentionText(checkAttention)} since this observer was drawn`,
                        // A null geometry is a refusal, not a clear — the flag stays until it can be
                        // judged. Not an edit: no `markPlanDirty()` and no undo step (R1); the record
                        // is saved quietly with the plan (`onboarding-polish-observer-notices`).
                        onConfirm: () => {
                          const geometry = geometryOf(sessionLayout, selected.refid);
                          // A null geometry is a refusal: the flag stays, and nothing is said.
                          if (!geometry) return;
                          stampObservers(selectedObservers, geometry);
                          // Said in the stage's one polite region: the button is gone, and the
                          // plan's dot is colour (`onboarding-polish-observer-notices`, AC10).
                          say(CONFIRMED_TEXT);
                        },
                      }
                    : undefined
                }
              />
            )
          : undefined
      }
      trackingCard={grip => (
        <TrackingCard
          grip={grip}
          title={trackTitle}
          value={trackValue}
          disabled={trackingNeedsPick}
          onChange={changeTrackSide}
        />
      )}
      shortcutCard={<ShortcutCard open={shortcutsOpen} id={shortcutsId} />}
      dock={grip => (
        <PictureDock
          grip={grip}
          zoom={pictureZoom}
          onRecentre={() => plotterRef.current?.recentre()}
          onRefresh={canRefreshPicture ? refreshPicture : undefined}
          refreshing={feedState === 'connecting'}
          shortcutsOpen={shortcutsOpen}
          onToggleShortcuts={() => setShortcutsOpen(o => !o)}
          shortcutsId={shortcutsId}
        />
      )}
    />
  ) : null;
  const cameraHeader = editorOpen
    ? {
        name: cameraName,
        pictureTime:
          selectedFeed?.imageUrl && selectedFeed.capturedAt
            ? clockTime(selectedFeed.capturedAt)
            : null,
      }
    : null;

  /*
   * A search pick is `pickEntity`, the navigator's own pick (FR5.3), judged as a pick FROM THE PLAN
   * (`fromPlan`): the search offers every entity on every floor, so the navigator's "every row in
   * this camera's list is this camera's" premise does not hold for it — taken as a camera-list pick,
   * it kept the camera on stage and linked a waiting region to an entity on another floor (found by
   * review). The floor switches only when the camera lets go (`floorIndex`). A feed opens exactly as
   * the Cameras view opens it (`openCameraWorkspace` switches floor itself).
   */
  const pickFromSearch = (entry: PlanSearchEntry) => {
    const feedId = feedIdByRefid.get(entry.refid);
    if (entry.kind === 'feed' && feedId != null) {
      openCameraWorkspace(feedId);
      return;
    }
    if (entry.kind === 'surface' || entry.kind === 'item') {
      pickEntity(entry.refid, entry.kind, { fromPlan: true, floorIndex: entry.floorIndex });
    }
  };
  /* The folded list's summary (R2.5): what needs attention, counted as the floors count it. */
  const attentionCount =
    observersStatus === 'ready' ? stageFloors.reduce((n, f) => n + f.attention, 0) : null;
  /*
   * The plan search lives in the list panel's search row (R2.2), not the nav bar, with `/` to focus
   * it and its key cap. Folded, the row is hidden, so the box claims nothing and the panel's own `/`
   * listener reopens it; the remove confirmation holds the key as the panel's shortcuts do.
   */
  const search = (
    <PlanEntitySearch
      shortcut={removeTarget ? undefined : '/'}
      entries={searchIndex}
      twinBid={twinBidParam}
      onPick={pickFromSearch}
      onLoadTypes={planSearch.loadTypes}
      typeStatus={planSearch.typeStatus}
    />
  );
  /* The likely cameras' Draw Observer (R3.6): the camera a tile press would open, armed. */
  const drawObserver = (endpointId: number) => {
    if (!stagePick || stage.view === 'plan' || stage.view === 'camera' || stage.linking) return;
    armOnEntryRef.current = endpointId;
    openEditor(stagePick.refid, stagePick.kind, endpointId);
  };
  /* The likely cameras' close (R3.7): one step back, exactly as Esc. */
  const closeCamerasPanel = () => {
    const back = stepBack(stage, !!stagePick, hasSuggestions);
    if (!back) return;
    setStage(back.view);
    if (back.clearSelection) setStagePick(null);
  };

  // Gate on the whole plan (all floors), not just the active floor: an empty active floor still
  // shows the stage's floor list and a blank plan, so the operator can switch back.
  if (floorEntityIndex.size === 0) {
    return (
      <div className={className}>
        {notices}
        <div className="gate-status">
          <Icon name="layer-group" size="2xl" className="status-icon" />
          <h3>Nothing on the plan yet</h3>
          <p>
            Observers are defined against the zones and equipment on your floor plan. Add some in
            the Floor Plan step first, then come back to set up observers.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {notices}
      <div className="stage-body" ref={stageBodyRef} tabIndex={-1}>
        {/* The inventory, as the shared list panel (R2): first in the tab order after the step list. */}
        <div className="inventory-slot" data-observer-navigator="" data-planner-shortcuts="off">
          <ListPanel
            name="Inventory"
            summary={
              attentionCount === null
                ? undefined
                : attentionCount > 0
                  ? `${attentionCount} need attention`
                  : 'Nothing needs attention'
            }
            width={LIST_PANEL_WIDTH}
            open={listOpen}
            onOpenChange={onListOpenChange}
            shortcutEnabled={!removeTarget}
            searchSlot={search}
            searchLabel="Search equipment, areas or a BID"
            header={<div ref={setInventoryHeader} data-inventory-header="" />}
          >
            {(() => {
              const panel = (
                <HoverLinkedPanel
                  hoverStore={hoverStore}
                  pictureHoverStore={pictureHoverStore}
                  headerHost={inventoryHeader}
                  floor={activeFloorTarget}
                  surfaces={surfaces}
                  items={items}
                  observerCounts={observerCounts}
                  countsLoading={countsLoading}
                  attention={attentionByRefid}
                  attentionSummary={attentionSummary}
                  observersStatus={observersStatus}
                  modelStatus={modelStatus}
                  addedStatus={
                    draftComparison === 'loading' || draftComparison === 'failed'
                      ? draftComparison
                      : 'ready'
                  }
                  selectedId={selected?.refid ?? stagePick?.refid ?? null}
                  onSelect={pickEntity}
                  feeds={feeds}
                  streamsStatus={streamsStatus}
                  observersByFeed={observersByFeed}
                  onOpenFeed={openCameraWorkspace}
                  onRefreshFeed={refreshFeed}
                  zoomRegionsByFeed={zoomRegionsByFeed}
                  // Camera by camera, the lists are the camera's own: what it sees and what it observes.
                  currentFeedId={workspace?.endpointId ?? null}
                  waiting={!!waitingRegion}
                  observersOnThisCamera={workspace ? observersOnThisCamera : undefined}
                  sight={
                    !workspace || !sessionLayout
                      ? undefined
                      : workspaceSight
                        ? { kind: 'in-view', refids: workspaceSight.inView }
                        : { kind: 'unplaced' }
                  }
                />
              );
              // While Suggestions are on, the camera's machines replace the list; the list stays as
              // the fallback where the engine can't place machines (`observer-autogen-review-walkthrough`).
              if (!reviewActive) return panel;
              return (
                <MachineList
                  review={review}
                  loading={reviewLoading}
                  error={reviewError}
                  onRetry={rereadSuggestions}
                  fallbackContent={panel}
                  selectedRefid={selectedMachineRefid}
                  onSelect={selectMachine}
                  onHover={setHoveredMachineRefid}
                  onUse={takeSuggestion}
                  onKeep={keepMine}
                  onForget={forgetKeep}
                  onDiscard={discardSuggestion}
                  onDiscardReason={setDiscardReason}
                  drawnPart={drawnPartFor}
                  onAdjust={adjustSuggestion}
                  onRelink={relinkSuggestion}
                  relinkOptions={relinkOptions}
                  relinkingRefid={relinkingRefid}
                  onToggleRelink={id => setRelinkingRefid(r => (r === id ? null : id))}
                  onAcceptAll={acceptAll}
                  showAll={showAllSuggestions}
                  onShowAll={setShowAllSuggestions}
                  showOutlines={showAllOutlines}
                  onShowOutlines={setShowAllOutlines}
                  shownUnexplainedId={shownUnexplainedId}
                  onShowUnexplained={setShownUnexplainedId}
                  days={
                    reviewCamera
                      ? {
                          binned: reviewCamera.daysBinned,
                          window: Number.parseInt(reviewCamera.window, 10),
                        }
                      : null
                  }
                />
              );
            })()}
          </ListPanel>
        </div>

        <ObserverStage
          className="stage"
          view={shownStage}
          entity={stageEntity}
          layout={sessionLayout}
          floorIndex={activeFloorIndex}
          floors={stageFloors}
          markers={markers}
          selectedId={selected?.refid ?? stagePick?.refid ?? null}
          onPickFromPlan={pickEntity}
          hoverStore={hoverStore}
          describeHover={describeHover}
          recentreSignal={recentreSignal}
          onRecentre={() => setRecentreSignal(n => n + 1)}
          observersStatus={observersStatus}
          modelFailed={modelStatus === 'failed'}
          feeds={feeds}
          streamsStatus={streamsStatus}
          scores={stageScores}
          onOpenFeed={openFromStage}
          onRefreshFeed={refreshFeed}
          zoomRegionsByFeed={zoomRegionsByFeed}
          onShowAllCameras={() =>
            stage.view !== 'plan' &&
            stage.view !== 'camera' &&
            setStage({ ...stage, view: 'all-cameras' })
          }
          onShowSuggestions={() =>
            stage.view !== 'plan' &&
            stage.view !== 'camera' &&
            setStage({ ...stage, view: 'suggestions' })
          }
          onAddCamera={addCameraFromStage}
          onPlanBack={editorOpen ? () => leaveCamera(PLAN_VIEW) : planBack}
          onGoToCameraFeeds={onGoToCameraFeeds}
          camera={cameraBody}
          cameraHeader={cameraHeader}
          note={stageNote}
          onPickFromCorner={pickFromCorner}
          listOpen={listOpen}
          showMap={showMap}
          onShowMapChange={setShowMap}
          onDrawObserver={drawObserver}
          onClosePanel={closeCamerasPanel}
          sideBySide={
            shownStage.view === 'side-by-side' && stagePick ? (
              <ObserverSideBySide
                entityLabel={stagePick.label}
                entityRefid={stagePick.refid}
                polygonsByEntity={polygonsByEntity}
                observerState={observerState}
                groups={stageGroups}
                feedById={feedById}
                cameraName={cameraNameOf}
                tipFor={stageTipFor}
                streamsStatus={streamsStatus}
                onEditCamera={openFromStage}
                onRemoveCamera={requestRemove}
                onRefreshFeed={refreshFeed}
                trailing={
                  <AddCameraTile entityLabel={stagePick.label} onAdd={addCameraFromStage} />
                }
              />
            ) : null
          }
        />
      </div>

      {removeTarget && removeCopy && (
        <Modal
          open
          onClose={() => setRemoveTarget(null)}
          returnFocusTo={removeOpenerRef}
          title={removeCopy.title}
          tone="coral"
          width="md"
          icon={<Icon name="video-slash" />}
          actions={
            <>
              <Button
                type="button"
                variant="frosted"
                autoFocus
                onClick={() => setRemoveTarget(null)}
              >
                Keep Camera
              </Button>
              <Button
                type="button"
                variant="destructive"
                iconLeft={<Icon name="trash-can" />}
                onClick={confirmRemove}
              >
                Remove Camera
              </Button>
            </>
          }
        >
          <RemoveBody>
            <p className="lead">{removeCopy.lead}</p>
            {removeCopy.regions.length > 0 && (
              <ul>
                {removeCopy.regions.map(r => (
                  <li key={r.refid}>
                    <Icon name="draw-polygon" />
                    <span className="name">{r.title}</span>
                    {removeCopy.code && (
                      <Tag surface="on-solid" tone="code">
                        {removeCopy.code}
                      </Tag>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p>{removeCopy.after}</p>
          </RemoveBody>
        </Modal>
      )}
    </div>
  );
};

/**
 * The list, with the plan's hovered entity glowing in it (FR15) — subscribed to the hover store here,
 * so a hover re-renders the list and never the whole step.
 */
const HoverLinkedPanel: React.FC<
  React.ComponentProps<typeof ObserverInventoryPanel> & {
    hoverStore: HoverStore;
    pictureHoverStore: HoverStore;
  }
> = ({ hoverStore, pictureHoverStore, ...props }) => {
  const hovered = useHovered(hoverStore);
  const onPicture = useHovered(pictureHoverStore);
  return (
    <ObserverInventoryPanel {...props} hoveredId={hovered?.refid ?? onPicture?.refid ?? null} />
  );
};

/** The Remove Camera confirm's body: what goes, listed, then what stays (FR27). */
const RemoveBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  ${typeRole.body1};
  color: ${semantic.text.primary};

  p {
    margin: 0;
  }
  .lead {
    font-weight: ${weight.medium};
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${space[1]};
  }
  li {
    display: flex;
    align-items: center;
    gap: ${space[2]};
  }
  .name {
    flex: 1;
    font-weight: ${weight.semibold};
  }
`;

export default styled(OnboardingObservers)`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* The notices sit ABOVE the panel-and-canvas row, so a failure is read before the counts it
     invalidates rather than beside them. */
  .stage-notices {
    flex-shrink: 0;
    padding: 0.75rem 1rem 0;
  }

  /* Its live regions are always in the DOM — a region added at the same moment as its content is
     not announced — so it says when it has nothing to show, and takes no room. */
  .stage-notices[data-has-notices='false'] {
    padding: 0;
  }

  /*
   * Under the glass (onboarding-persistent-scene-observers-glass, R1): the step area is the
   * window, the plan fills it, and every panel floats over it.
   */
  .stage-body {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    /* Its own stacking context: the design-system Modal's root carries no z-index, so a layer in
       here with one would paint above a dialog opened over the step (camera-first-observers). */
    isolation: isolate;
  }

  /*
   * The inventory list panel's place (R2.1): beside the step list, below the bar, at the 8 px edge,
   * above the plan. The slot passes pointers through, so the plan beside a folded bar still takes
   * them; the panel takes its own back.
   */
  .inventory-slot {
    position: absolute;
    top: ${PANEL_TOP};
    left: ${GLASS_START};
    bottom: ${GLASS_EDGE};
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    pointer-events: none;
  }

  .gate-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    flex: 1;
    padding: 2rem;
    text-align: center;

    .status-icon {
      width: 40px;
      height: 40px;
      color: #a3a3a3;
    }

    h3 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1a1a1a;
    }

    p {
      margin: 0;
      max-width: 420px;
      font-size: 0.9rem;
      color: #707070;
    }
  }
`;
